import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  ShieldCheck,
  ShoppingBag,
  Package,
  Upload,
  MapPin,
  Users,
  UserCheck,
  Wallet,
  Settings as SettingsIcon,
  Save,
  X,
  Eye,
  Send,
  Scale,
  Instagram,
  Trophy,
  Check,
  Copy,
  Snowflake,
  Dice5,
  ScanLine,
  FolderTree,
  Image as ImageIcon,
  Sparkles,
  FileSpreadsheet,
} from "lucide-react";
import { useNewOrderWatcher } from "@/hooks/useNewOrderWatcher";
import BarcodeScannerModal from "@/components/admin/BarcodeScannerModal";
import BlacklistDialog from "@/components/admin/BlacklistDialog";
import ProductImageDialog from "@/components/admin/ProductImageDialog";
import AdminsPanel from "@/components/admin/AdminsPanel";
import AdminErrorBoundary from "@/components/admin/ErrorBoundary";
import BulkMoveCategoryDialog from "@/components/admin/BulkMoveCategoryDialog";
import FilterDeleteDialog from "@/components/admin/FilterDeleteDialog";
import WolvoxGroupReportDialog from "@/components/admin/WolvoxGroupReportDialog";
import SponsorBannersPanel from "@/components/admin/SponsorBannersPanel";
import HandmadePanel from "@/components/admin/HandmadePanel";
import ExcelCatalogDialog from "@/components/admin/ExcelCatalogDialog";
import QuickAddDialog from "@/components/admin/QuickAddDialog";
import ProductSearchDialog from "@/components/admin/ProductSearchDialog";

const STATUS_OPTIONS = [
  { v: "received", l: "Alındı" },
  { v: "preparing", l: "Hazırlanıyor" },
  { v: "on_the_way", l: "Kurye Yolda" },
  { v: "delivered", l: "Teslim Edildi" },
  { v: "cancelled", l: "İptal" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirect = window.location.origin + window.location.pathname;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

export default function Admin() {
  const { t, user, authLoading, logout, settings, refreshSettings } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [discountRequests, setDiscountRequests] = useState([]);
  const [raffles, setRaffles] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  // Kategori bekleyen (yeni Akınsoft ürünleri) için header rozeti sayacı.
  // 60 sn'de bir yenilenir, admin ürün ekranına geçerse 10 sn'de bir.
  const [needsCatCount, setNeedsCatCount] = useState(0);
  // ProductsPanel'e override — rozete basınca kategori filtresi "yeni-urunler" olsun.
  const [productCatOverride, setProductCatOverride] = useState(null);
  const isAdmin = user?.is_admin;

  // New-order watcher: red badge + beep + title flash every 15 s
  const { newCount, markAllSeen, playTestBeep, audioReady } = useNewOrderWatcher({ enabled: !!isAdmin, intervalMs: 15000 });

  // Clear the badge when the admin actually looks at the Siparişler tab
  useEffect(() => {
    if (activeTab === "orders" && newCount > 0) {
      markAllSeen(orders);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, orders]);

  const [categoryCounts, setCategoryCounts] = useState({});
  // Default: admin sadece AKTİF ürünleri görür. "Pasifliler" toggle ile pasifler yüklenir.
  // 13K+ pasif ürünü sürekli göstermek yerine admin ihtiyaç halinde açar.
  const [showInactive, setShowInactive] = useState(false);
  const loadAll = async () => {
    try {
      const prodQuery = showInactive
        ? "/products?limit=20000&include_inactive=true&include_zero_price=true"
        : "/products?limit=20000";
      const [o, p, c, cr, cu, dr, ra, cc] = await Promise.all([
        api.get("/orders"),
        api.get(prodQuery),
        api.get("/categories?include_hidden=true"),
        api.get("/couriers"),
        api.get("/customers"),
        api.get("/discount-requests"),
        api.get("/raffles"),
        api.get("/admin/categories/counts").catch(() => ({ data: {} })),
      ]);
      setOrders(o.data);
      setProducts(p.data);
      setCategories(c.data);
      setCouriers(cr.data);
      setCustomers(cu.data);
      setDiscountRequests(dr.data);
      setRaffles(ra.data);
      setCategoryCounts(cc.data || {});
    } catch (e) {
      if ([401, 403].includes(e.response?.status)) toast.error("Yetkiniz yok");
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, showInactive]);

  // Kategori bekleyen ürün sayacı — periyodik poll
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/admin/products/needs-category/count");
        if (!cancelled) setNeedsCatCount(r.data?.count || 0);
      } catch {}
    };
    tick();
    const interval = activeTab === "products" ? 10000 : 60000;
    const id = setInterval(tick, interval);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin, activeTab]);

  const openNeedsCategoryQueue = () => {
    setActiveTab("products");
    setProductCatOverride("yeni-urunler");
  };

  const updateOrder = async (id, patch) => {
    try {
      await api.patch(`/orders/${id}`, patch);
      toast.success("Güncellendi");
      loadAll();
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Ürün silindi");
      loadAll();
    } catch {
      toast.error("Silinemedi");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-[#2E5A36] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white border border-[#E8E2D5] rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 rounded-full bg-[#2E5A36] flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-[#E5A024]" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl text-[#1B2A22] mb-2">
              {t("admin_dashboard")}
            </h1>
            <p className="text-sm text-[#1B2A22]/60 mb-6">{t("admin_login_note")}</p>
            <Button
              data-testid="admin-login-btn"
              onClick={startLogin}
              className="w-full h-12 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full font-heading font-bold"
            >
              {t("admin_login")}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-8 text-center">
            <h1 className="font-heading font-bold text-xl text-red-700 mb-2">
              🚫 {t("unauthorized_email")}
            </h1>
            <p className="text-sm text-[#1B2A22]/60">{user.email}</p>
            <Button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              variant="outline"
              className="mt-4 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1" />
              {t("logout")}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <Header />
      <AdminErrorBoundary>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-[#1B2A22]">
              {t("admin_dashboard")}
            </h1>
            <p className="text-sm text-[#1B2A22]/60">
              {user.email} · <span className="text-[#2E5A36] font-semibold">Admin</span>
            </p>
          </div>
          <Button
            data-testid="admin-sound-test-btn"
            onClick={playTestBeep}
            variant="outline"
            className={`rounded-full ${audioReady ? "border-[#2E5A36] text-[#2E5A36]" : "border-[#E5A024] text-[#B4711C] animate-pulse"} bg-white`}
            title={audioReady ? "Yeni sipariş sesini test et" : "Sipariş sesini açmak için TIKLA (tarayıcı güvenlik kuralı)"}
          >
            {audioReady ? "🔊 Sesi Test Et" : "🔕 Sesi Aç"}
          </Button>
          <Button
            data-testid="admin-logout-btn"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            variant="outline"
            className="rounded-full border-[#E8E2D5]"
          >
            <LogOut className="w-4 h-4 mr-1" />
            {t("logout")}
          </Button>
          <Button
            data-testid="admin-go-home-btn"
            onClick={() => window.open("/", "_blank", "noopener")}
            variant="outline"
            className="rounded-full border-[#2E5A36] text-[#2E5A36] bg-white"
            title="Ana sayfayı yeni sekmede aç — panel oturumu korunur"
          >
            🏠 Ana Sayfa
          </Button>
          {needsCatCount > 0 && (
            <Button
              data-testid="needs-category-badge-btn"
              onClick={openNeedsCategoryQueue}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-heading font-bold animate-pulse shadow-lg"
              title="Akınsoft'tan gelen kategorisi belirsiz ürünler — tıkla ve ata"
            >
              🆕 {needsCatCount} ürün kategori bekliyor
            </Button>
          )}
          <Button
            data-testid="toggle-inactive-btn"
            onClick={() => setShowInactive((v) => !v)}
            variant="outline"
            className={`rounded-full font-heading font-bold ${
              showInactive
                ? "bg-[#1B2A22] text-white border-[#1B2A22]"
                : "border-[#1B2A22] text-[#1B2A22] bg-white"
            }`}
            title={
              showInactive
                ? "Pasif ürünleri de yüklüyorsun — kapatınca sadece aktif + fiyatlılar yüklenir"
                : "Pasif ve fiyatsız ürünleri de görmek için aç (yükleme uzayabilir)"
            }
          >
            {showInactive ? "👁 Pasifliler Açık" : "🔕 Pasifliler Gizli"}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { icon: ShoppingBag, label: "Toplam Sipariş", value: orders.length },
            {
              icon: Package,
              label: "Aktif Sipariş",
              value: orders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length,
            },
            { icon: Package, label: "Ürün Sayısı", value: products.length },
            {
              icon: Wallet,
              label: "Bugünkü Ciro",
              value: `₺${orders
                .filter((o) => o.created_at?.startsWith(new Date().toISOString().slice(0, 10)))
                .filter((o) => o.status === "delivered")
                .reduce((s, o) => s + o.total, 0)
                .toFixed(2)}`,
            },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`stat-card-${i}`}
              className="bg-white border border-[#E8E2D5] rounded-2xl p-4"
            >
              <s.icon className="w-4 h-4 text-[#2E5A36] mb-2" />
              <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#2E5A36]">
                {s.label}
              </p>
              <p className="font-mono font-extrabold text-2xl text-[#1B2A22] mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E8E2D5] rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="orders" data-testid="tab-orders" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold relative">
              <ShoppingBag className="w-4 h-4 mr-1" /> {t("orders")}
              {newCount > 0 && (
                <span
                  data-testid="new-orders-badge"
                  className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-mono font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse ring-2 ring-white"
                  title={`${newCount} yeni sipariş`}
                >
                  {newCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="archive" data-testid="tab-archive" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <Package className="w-4 h-4 mr-1" /> Arşiv (7 gün)
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <Package className="w-4 h-4 mr-1" /> {t("products")}
            </TabsTrigger>
            <TabsTrigger value="couriers" data-testid="tab-couriers" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <Users className="w-4 h-4 mr-1" /> {t("couriers")}
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <UserCheck className="w-4 h-4 mr-1" /> {t("customers")}
            </TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold relative">
              <Instagram className="w-4 h-4 mr-1" /> {t("discount_requests")}
              {discountRequests.filter((r) => r.status === "pending").length > 0 && (
                <span data-testid="pending-discount-badge" className="absolute -top-1 -right-1 bg-[#D9653B] text-white text-[10px] font-mono font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {discountRequests.filter((r) => r.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="raffles" data-testid="tab-raffles" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <Trophy className="w-4 h-4 mr-1" /> {t("raffles")}
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <Wallet className="w-4 h-4 mr-1" /> {t("reports")}
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <FolderTree className="w-4 h-4 mr-1" /> Kategoriler
            </TabsTrigger>
            <TabsTrigger value="stocount" data-testid="tab-stocount" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <ScanLine className="w-4 h-4 mr-1" /> Stok Sayımı
            </TabsTrigger>
            <TabsTrigger value="handmade" data-testid="tab-handmade" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <Sparkles className="w-4 h-4 mr-1" /> El Yapımı
            </TabsTrigger>
            <TabsTrigger value="admins" data-testid="tab-admins" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              🛡️ Yöneticiler
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[#2E5A36] data-[state=active]:text-[#FAF7F2] rounded-full font-heading font-semibold">
              <SettingsIcon className="w-4 h-4 mr-1" /> {t("settings")}
            </TabsTrigger>
          </TabsList>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-4">
            <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Müşteri</TableHead>
                    <TableHead>Ödeme</TableHead>
                    <TableHead>Toplam</TableHead>
                    <TableHead>Kurye</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Konum</TableHead>
                    <TableHead>İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-[#1B2A22]/60">
                        Henüz sipariş yok.
                      </TableCell>
                    </TableRow>
                  )}
                  {orders.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                      <TableCell className="font-mono font-bold text-[#2E5A36]">
                        #{o.order_number}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-semibold">{o.customer_name}</p>
                          <p className="text-xs text-[#1B2A22]/60">{o.phone}</p>
                          <p className="text-xs text-[#1B2A22]/60 line-clamp-1">{o.address}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={o.payment_method === "cash" ? "bg-[#E5A024]/20 text-[#B4711C] hover:bg-[#E5A024]/20" : "bg-[#2E5A36]/20 text-[#2E5A36] hover:bg-[#2E5A36]/20"}>
                          {o.payment_method === "cash" ? "Nakit" : "Kart"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono font-bold">₺{o.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <Select
                          value={o.courier_id || "none"}
                          onValueChange={(v) => updateOrder(o.id, { courier_id: v === "none" ? "" : v })}
                        >
                          <SelectTrigger data-testid={`courier-select-${o.id}`} className="w-32 h-9 bg-[#FAF7F2] border-[#E8E2D5]">
                            <SelectValue placeholder={t("unassigned")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— {t("unassigned")}</SelectItem>
                            {couriers.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={o.status}
                          onValueChange={(v) => updateOrder(o.id, { status: v })}
                        >
                          <SelectTrigger data-testid={`order-status-select-${o.id}`} className="w-36 h-9 bg-[#FAF7F2] border-[#E8E2D5]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {o.gps_link ? (
                          <a href={o.gps_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2E5A36] text-xs font-semibold" data-testid={`order-gps-${o.id}`}>
                            <MapPin className="w-3.5 h-3.5" /> Harita
                          </a>
                        ) : (
                          <span className="text-xs text-[#1B2A22]/40">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <OrderActions
                          order={o}
                          courier={couriers.find((c) => c.id === o.courier_id)}
                          onOpen={() => setDetailsOrder(o)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ARCHIVE */}
          <TabsContent value="archive" className="mt-4">
            <ArchivePanel />
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-4">
            <ProductsPanel
              products={products}
              categories={categories}
              onReload={loadAll}
              onEdit={setEditingProduct}
              onCreate={() => setCreatingProduct(true)}
              onScan={() => setScannerOpen(true)}
              overrideCatFilter={productCatOverride}
              onConsumeOverride={() => setProductCatOverride(null)}
            />
          </TabsContent>

          {/* COURIERS */}
          <TabsContent value="couriers" className="mt-4">
            <CourierPanel couriers={couriers} onChange={loadAll} />
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-4">
            <CustomerPanel customers={customers} orders={orders} />
          </TabsContent>

          {/* DISCOUNTS */}
          <TabsContent value="discounts" className="mt-4">
            <DiscountPanel requests={discountRequests} onChange={loadAll} />
          </TabsContent>

          {/* RAFFLES */}
          <TabsContent value="raffles" className="mt-4">
            <RafflePanel raffles={raffles} customers={customers} onChange={loadAll} />
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports" className="mt-4">
            <ReportPanel />
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="mt-4">
            <CategoriesPanel categories={categories} products={products} categoryCounts={categoryCounts} onChanged={loadAll} />
          </TabsContent>

          {/* STOCK COUNT */}
          <TabsContent value="stocount" className="mt-4">
            <StockCountPanel />
          </TabsContent>

          <TabsContent value="handmade" className="mt-4">
            <HandmadePanel />
          </TabsContent>

          {/* ADMINS */}
          <TabsContent value="admins" className="mt-4">
            <AdminsPanel />
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-4">
            <SettingsPanel settings={settings} refresh={refreshSettings} />
          </TabsContent>
        </Tabs>
      </main>
      </AdminErrorBoundary>

      <ProductFormDialog
        open={!!editingProduct || creatingProduct}
        product={editingProduct}
        categories={categories}
        onClose={() => {
          // Same anti-race as ProductImageDialog: let Radix finish animating
          // the close before we null out `product`, otherwise image <img>
          // inside the form can collide with the portal unmount.
          setTimeout(() => {
            setEditingProduct(null);
            setCreatingProduct(false);
          }, 220);
        }}
        onSaved={() => {
          setEditingProduct(null);
          setCreatingProduct(false);
          loadAll();
        }}
      />

      <OrderDetailsDialog
        order={detailsOrder}
        couriers={couriers}
        onClose={() => setDetailsOrder(null)}
      />

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanned={(code) => {
          setScannerOpen(false);
          const match = products.find(
            (p) =>
              (p.barcode && p.barcode === code) ||
              (p.akinsoft_code && p.akinsoft_code === code)
          );
          if (match) {
            toast.success(`Ürün bulundu: ${match.name_tr}`);
            setEditingProduct(match);
          } else {
            toast.error(`Barkod bulunamadı: ${code} — yeni ürün olarak ekle?`);
            setCreatingProduct(true);
            // prefill barcode in next tick (ProductForm reads product/null → use state)
            setTimeout(() => {
              const inp = document.querySelector('[data-testid="pf-barcode"]');
              if (inp) {
                const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
                setter.call(inp, code);
                inp.dispatchEvent(new Event("input", { bubbles: true }));
              }
            }, 300);
          }
        }}
      />
    </div>
  );
}

function buildCourierMessage(order, courier) {
  const lines = [];
  lines.push(`[İKİZLER MARKET — TESLİMAT FİŞİ]`);
  lines.push(`Sipariş No: *${order.order_number}*`);
  if (courier?.name) lines.push(`Kurye: ${courier.name}`);
  lines.push(``);
  lines.push(`*Müşteri:* ${order.customer_name}`);
  lines.push(`*Telefon:* ${order.phone}`);
  lines.push(`*Adres:* ${order.address}`);
  if (order.gps_link) lines.push(`*Konum:* ${order.gps_link}`);
  lines.push(``);
  lines.push(`*Ürünler:*`);
  (order.items || []).forEach((i) => {
    lines.push(`• ${i.quantity} x ${i.name} — ₺${(i.price * i.quantity).toFixed(2)}`);
  });
  if (order.notes) {
    lines.push(``);
    lines.push(`*Not:* ${order.notes}`);
  }
  lines.push(``);
  const pay = order.payment_method === "cash" ? "NAKİT" : "KREDİ KARTI";
  lines.push(`*TAHSİL EDİLECEK:* ₺${order.total.toFixed(2)}  (${pay})`);
  // Delivery completion link (kurye paneli — Phase 2)
  if (order.delivery_token) {
    const origin = (typeof window !== "undefined" && window.location?.origin) || "https://ikizlermarketlefke.com";
    lines.push(``);
    lines.push(`⚡ Teslim edince TIKLA:`);
    lines.push(`${origin}/kurye/t/${order.delivery_token}`);
  }
  return lines.join("\n");
}

function toWaNumber(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  // Cyprus/Turkey numbers: assume country code prefix if starts with 5 → prepend 90
  if (digits.startsWith("90") || digits.startsWith("357")) return digits;
  if (digits.startsWith("0")) return "90" + digits.slice(1);
  if (digits.startsWith("5") && digits.length === 10) return "90" + digits;
  return digits;
}

function OrderActions({ order, courier, onOpen }) {
  const hasCourier = !!courier;
  const hasPhone = !!(courier && courier.phone);

  const sendToCourier = () => {
    if (!hasCourier) {
      toast.error("Önce bir kurye atayın");
      return;
    }
    if (!hasPhone) {
      toast.error(`${courier.name} için telefon numarası tanımlı değil. Kuryeler sekmesinden ekleyin.`);
      return;
    }
    const msg = buildCourierMessage(order, courier);
    const number = toWaNumber(courier.phone);
    const url = `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="flex gap-1">
      <Button
        data-testid={`order-details-btn-${order.id}`}
        size="sm"
        variant="outline"
        onClick={onOpen}
        className="rounded-full h-8 px-2 border-[#E8E2D5]"
        title="Detayları Gör"
      >
        <Eye className="w-3.5 h-3.5" />
      </Button>
      <Button
        data-testid={`send-courier-btn-${order.id}`}
        size="sm"
        onClick={sendToCourier}
        disabled={!hasCourier}
        className="rounded-full h-8 px-2 bg-[#25D366] hover:bg-[#1DA851] text-white disabled:opacity-40 disabled:bg-[#25D366]"
        title={hasCourier ? "Kuryeye WhatsApp Gönder" : "Önce kurye atayın"}
      >
        <Send className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function OrderDetailsDialog({ order, couriers, onClose }) {
  if (!order) return null;
  const courier = couriers.find((c) => c.id === order.courier_id);
  const hasPhone = !!courier?.phone;

  const send = () => {
    if (!courier) return toast.error("Kurye atanmamış");
    if (!hasPhone) return toast.error(`${courier.name} için telefon tanımlı değil.`);
    const msg = buildCourierMessage(order, courier);
    const number = toWaNumber(courier.phone);
    window.open(
      `https://wa.me/${number}?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener"
    );
  };

  const statusLabel = STATUS_OPTIONS.find((s) => s.v === order.status)?.l || order.status;

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="order-details-dialog"
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <span className="font-mono text-[#2E5A36]">#{order.order_number}</span>
            <Badge className="bg-[#E8E2D5] text-[#1B2A22] hover:bg-[#E8E2D5]">
              {statusLabel}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Müşteri</p>
            <p className="font-semibold text-[#1B2A22] mt-1">{order.customer_name}</p>
            <a
              href={`tel:${order.phone}`}
              className="text-sm text-[#2E5A36] font-mono underline"
              data-testid="order-customer-phone"
            >
              {order.phone}
            </a>
            <p className="text-sm text-[#1B2A22]/80 mt-2 break-words">{order.address}</p>
            {order.gps_link && (
              <a
                href={order.gps_link}
                target="_blank"
                rel="noreferrer"
                data-testid="order-details-gps"
                className="inline-flex items-center gap-1 mt-2 text-[#D9653B] text-sm font-bold"
              >
                <MapPin className="w-4 h-4" /> Anlık Konumu Haritada Aç
              </a>
            )}
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36] mb-2">
              Ürünler
            </p>
            <ul className="space-y-1 text-sm">
              {(order.items || []).map((i, idx) => (
                <li key={idx} className="flex justify-between gap-2">
                  <span className="flex-1">
                    <span className="font-mono font-bold text-[#2E5A36]">{i.quantity}×</span>{" "}
                    {i.name}
                  </span>
                  <span className="font-mono">₺{(i.price * i.quantity).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            {order.notes && (
              <div className="mt-3 pt-3 border-t border-[#E8E2D5]">
                <p className="text-[10px] uppercase font-heading font-bold text-[#D9653B]">Not</p>
                <p className="text-sm text-[#1B2A22]/80 mt-1">{order.notes}</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#1B2A22]/70">Ara Toplam</span>
              <span className="font-mono">₺{order.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-[#E8E2D5]">
              <span className="font-heading font-bold">Tahsil Edilecek</span>
              <span
                data-testid="order-collect-amount"
                className="font-mono font-extrabold text-[#D9653B] text-xl"
              >
                ₺{order.total.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-[#1B2A22]/60 text-right">
              {order.payment_method === "cash" ? "💵 Kapıda Nakit" : "💳 Kapıda Kredi Kartı"}
            </p>
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Kurye</p>
            {courier ? (
              <div className="mt-1">
                <p className="font-semibold">{courier.name}</p>
                <p className="text-sm text-[#1B2A22]/70 font-mono">
                  {courier.phone || "— Telefon tanımlı değil —"}
                </p>
              </div>
            ) : (
              <p className="text-sm text-[#1B2A22]/50 mt-1">
                Atanmamış · Önce kurye atayın
              </p>
            )}
          </div>

          <Button
            data-testid="order-send-wa-btn"
            onClick={send}
            disabled={!courier || !hasPhone}
            className="w-full h-12 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full font-heading font-bold disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-2" />
            Kuryeye WhatsApp ile Fişi Gönder
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExcelImport({ onDone }) {
  const { t } = useApp();
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/products/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`✓ ${data.created} yeni, ${data.updated} güncelleme`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={onFile}
        className="hidden"
        data-testid="excel-import-input"
      />
      <Button
        data-testid="excel-import-btn"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        variant="outline"
        className="rounded-full border-[#2E5A36] text-[#2E5A36] font-heading"
      >
        <Upload className="w-4 h-4 mr-1" />
        {uploading ? "Yükleniyor..." : t("import_excel")}
      </Button>
    </div>
  );
}

function ArchivePanel() {
  const [data, setData] = useState({ days: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/archive");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!window.confirm("24 saatten eski aktif siparişler arşive taşınacak. Devam?")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/archive-orders/run");
      toast.success(`${data.moved} taşındı, ${data.purged} silindi`);
      await load();
    } catch (e) {
      toast.error("Çalıştırılamadı");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-2xl p-3 text-xs text-[#B4711C] flex items-center justify-between gap-2">
        <span>🗂️ Siparişler her gece <b>01:00</b>'de arşive taşınır. Arşiv <b>7 gün</b> saklanır, sonra silinir.</span>
        <Button data-testid="run-archive-btn" size="sm" onClick={runNow} disabled={running} className="rounded-full bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2]">
          {running ? "..." : "Şimdi Çalıştır"}
        </Button>
      </div>
      {loading ? (
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-8 text-center text-[#1B2A22]/60">
          Yükleniyor...
        </div>
      ) : data.days.length === 0 ? (
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-8 text-center text-[#1B2A22]/60">
          Arşivde henüz sipariş yok.
        </div>
      ) : (
        <div className="grid gap-3">
          {data.days.map((day) => (
            <details key={day.date} data-testid={`archive-day-${day.date}`} className="bg-white border border-[#E8E2D5] rounded-2xl p-3 group">
              <summary className="cursor-pointer flex items-center justify-between gap-2 list-none">
                <div className="flex items-center gap-3">
                  <Package className="w-4 h-4 text-[#2E5A36]" />
                  <span className="font-heading font-bold">{day.date}</span>
                  <Badge className="bg-[#2E5A36]/10 text-[#2E5A36]">{day.count} sipariş</Badge>
                </div>
                <span className="font-mono font-extrabold text-[#2E5A36]">₺{day.total.toFixed(2)}</span>
              </summary>
              <div className="mt-3 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>Toplam</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Saat</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {day.orders.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell className="font-mono font-bold text-[#2E5A36]">#{o.order_number}</TableCell>
                        <TableCell>{o.customer_name}</TableCell>
                        <TableCell className="font-mono">₺{Number(o.total).toFixed(2)}</TableCell>
                        <TableCell><Badge className="text-[10px]">{o.status}</Badge></TableCell>
                        <TableCell className="text-xs text-[#1B2A22]/60">{(o.created_at || "").slice(11, 16)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerPanel({ customers, orders }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [detail, setDetail] = useState(null);

  const filtered = customers
    .filter((c) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      return (
        String(c.customer_number).includes(s) ||
        (c.phone || "").toLowerCase().includes(s) ||
        (c.first_name || "").toLowerCase().includes(s) ||
        (c.last_name || "").toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      if (sort === "spent") return (b.total_spent || 0) - (a.total_spent || 0);
      if (sort === "orders") return (b.order_count || 0) - (a.order_count || 0);
      return String(b.last_order_at || "").localeCompare(String(a.last_order_at || ""));
    });

  return (
    <div className="space-y-3">
      <div className="bg-white border border-[#E8E2D5] rounded-2xl p-3 flex gap-2 flex-wrap">
        <Input
          data-testid="customer-search"
          placeholder="Müşteri no, isim veya telefon..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bg-[#FAF7F2] flex-1 min-w-[200px]"
        />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="customer-sort" className="w-48 bg-[#FAF7F2]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Son sipariş</SelectItem>
            <SelectItem value="spent">Toplam harcama</SelectItem>
            <SelectItem value="orders">Sipariş sayısı</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Ad Soyad</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Sipariş</TableHead>
              <TableHead>Toplam</TableHead>
              <TableHead>İndirim</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-[#1B2A22]/60">
                  Kayıtlı müşteri yok.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.customer_number} data-testid={`customer-row-${c.customer_number}`}>
                <TableCell className="font-mono font-extrabold text-[#2E5A36]">
                  #{String(c.customer_number).padStart(4, "0")}
                </TableCell>
                <TableCell className="font-semibold">
                  {c.first_name} {c.last_name}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  <a href={`tel:${c.phone}`} className="text-[#2E5A36] underline">{c.phone}</a>
                </TableCell>
                <TableCell className="font-mono">{c.order_count}</TableCell>
                <TableCell className="font-mono font-bold">
                  ₺{Number(c.total_spent || 0).toFixed(2)}
                </TableCell>
                <TableCell>
                  {c.discount_used ? (
                    <Badge className="bg-[#1B2A22] text-[#FAF7F2] text-[10px]">Kullandı</Badge>
                  ) : c.discount_credit ? (
                    <Badge className="bg-[#2E5A36] text-white text-[10px]">Onaylı</Badge>
                  ) : (
                    <span className="text-xs text-[#1B2A22]/40">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    data-testid={`customer-detail-btn-${c.customer_number}`}
                    size="sm"
                    variant="outline"
                    onClick={() => setDetail(c)}
                    className="rounded-full h-8"
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CustomerDetailDialog customer={detail} orders={orders} onClose={() => setDetail(null)} />
    </div>
  );
}

function CustomerDetailDialog({ customer, orders, onClose }) {
  if (!customer) return null;
  const cn = customer.customer_number;
  const myOrders = (orders || [])
    .filter((o) => o.customer_number === cn)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="customer-detail-dialog"
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <span className="font-mono text-[#2E5A36]">#{String(cn).padStart(4, "0")}</span>
            <span>{customer.first_name} {customer.last_name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
              <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Sipariş</p>
              <p className="font-mono font-extrabold text-2xl">{customer.order_count || 0}</p>
            </div>
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
              <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Toplam</p>
              <p className="font-mono font-extrabold text-2xl">₺{Number(customer.total_spent || 0).toFixed(0)}</p>
            </div>
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
              <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Telefon</p>
              <a href={`tel:${customer.phone}`} className="text-sm font-mono text-[#2E5A36] underline break-all">
                {customer.phone}
              </a>
            </div>
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36] mb-2">
              Adresler ({customer.addresses?.length || 0})
            </p>
            <ul className="space-y-1 text-sm">
              {(customer.addresses || []).map((a, i) => (
                <li key={i} className="text-[#1B2A22]/80">• {a.formatted}</li>
              ))}
              {(!customer.addresses || customer.addresses.length === 0) && (
                <li className="text-[#1B2A22]/50 italic">Adres yok</li>
              )}
            </ul>
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36] mb-2">
              Sipariş Geçmişi
            </p>
            {myOrders.length === 0 ? (
              <p className="text-sm text-[#1B2A22]/50 italic">Sipariş yok</p>
            ) : (
              <ul className="space-y-2">
                {myOrders.slice(0, 20).map((o) => (
                  <li key={o.id} className="flex justify-between items-center text-sm border-b border-[#E8E2D5] pb-1 last:border-0">
                    <div>
                      <span className="font-mono font-bold text-[#2E5A36]">#{o.order_number}</span>
                      <span className="text-xs text-[#1B2A22]/60 ml-2">{(o.created_at || "").slice(0, 10)}</span>
                    </div>
                    <span className="font-mono font-bold">₺{o.total.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DiscountPanel({ requests, onChange }) {
  const [tab, setTab] = useState("pending");
  const filtered = requests.filter((r) => (tab === "all" ? true : r.status === tab));

  const review = async (id, status) => {
    const note = status === "rejected" ? window.prompt("Neden reddediyorsunuz? (opsiyonel)") || "" : "";
    try {
      await api.patch(`/discount-requests/${id}`, { status, admin_note: note });
      toast.success(status === "approved" ? "Onaylandı — müşteri bir sonraki siparişinde %10 kazanır" : "Reddedildi");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-2xl p-3 text-xs text-[#B4711C]">
        💡 <b>İpucu:</b> Talep gelince Instagram'da müşterinin sayfamızı takip ettiğini + son gönderiye 3 arkadaş etiketlediğini doğrulayın, sonra <b>Onayla</b>'ya basın. Onay verildiğinde müşteri <b>bir sonraki siparişinde</b> otomatik %10 indirim kazanır. Aynı müşteri numarasına ikinci bir indirim verilmez.
      </div>

      <div className="flex gap-2">
        {[
          { v: "pending", l: "Bekleyen" },
          { v: "approved", l: "Onaylı" },
          { v: "rejected", l: "Reddedilen" },
          { v: "all", l: "Hepsi" },
        ].map((s) => (
          <button
            key={s.v}
            data-testid={`discount-tab-${s.v}`}
            onClick={() => setTab(s.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-heading font-bold ${
              tab === s.v ? "bg-[#2E5A36] text-[#FAF7F2]" : "bg-white text-[#1B2A22] border border-[#E8E2D5]"
            }`}
          >
            {s.l}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Müşteri</TableHead>
              <TableHead>Instagram</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Tarih</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead>İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-[#1B2A22]/60">
                  Kayıt yok.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`discount-row-${r.id}`}>
                <TableCell className="font-mono font-bold text-[#2E5A36]">
                  #{String(r.customer_number).padStart(4, "0")}
                </TableCell>
                <TableCell>
                  <a
                    href={`https://www.instagram.com/${r.instagram_username}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#DD2A7B] font-semibold underline"
                    data-testid={`ig-open-${r.id}`}
                  >
                    @{r.instagram_username}
                  </a>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.phone}</TableCell>
                <TableCell className="text-xs text-[#1B2A22]/60">
                  {(r.created_at || "").slice(0, 16).replace("T", " ")}
                </TableCell>
                <TableCell>
                  <Badge
                    className={
                      r.status === "pending"
                        ? "bg-[#E5A024] text-white"
                        : r.status === "approved"
                        ? "bg-[#2E5A36] text-[#FAF7F2]"
                        : "bg-[#1B2A22]/50 text-white"
                    }
                  >
                    {r.status === "pending" ? "Bekliyor" : r.status === "approved" ? "Onaylı" : "Reddedildi"}
                  </Badge>
                </TableCell>
                <TableCell className="flex gap-1">
                  {r.status === "pending" ? (
                    <>
                      <Button
                        data-testid={`approve-discount-${r.id}`}
                        size="sm"
                        onClick={() => review(r.id, "approved")}
                        className="rounded-full h-8 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2]"
                      >
                        <Check className="w-3 h-3 mr-1" /> Onay
                      </Button>
                      <Button
                        data-testid={`reject-discount-${r.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => review(r.id, "rejected")}
                        className="rounded-full h-8 text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  ) : r.applied_to_order_number ? (
                    <span className="text-xs text-[#1B2A22]/60">Sipariş #{r.applied_to_order_number}</span>
                  ) : (
                    <span className="text-xs text-[#1B2A22]/40">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RafflePanel({ raffles, customers, onChange }) {
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-[#1B2A22]/60">
          🎁 Müşteri numaraları ile şeffaf çekiliş — deterministik, doğrulanabilir seed ile.
        </p>
        <Button
          data-testid="new-raffle-btn"
          onClick={() => setCreating(true)}
          className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Çekiliş
        </Button>
      </div>

      {creating && <RaffleForm onClose={() => setCreating(false)} onSaved={onChange} />}

      <div className="grid gap-3">
        {raffles.length === 0 && !creating && (
          <div className="bg-white border border-dashed border-[#E8E2D5] rounded-2xl p-8 text-center text-[#1B2A22]/60">
            Henüz çekiliş oluşturulmadı.
          </div>
        )}
        {raffles.map((r) => (
          <RaffleCard key={r.id} raffle={r} customerCount={customers.length} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function RaffleForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    prize: "",
    winner_count: 1,
    date_from: "",
    date_to: "",
    min_order_count: "",
    min_total_spent: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Başlık gerekli");
    const rules = {};
    if (form.date_from) rules.date_from = form.date_from;
    if (form.date_to) rules.date_to = form.date_to;
    if (form.min_order_count) rules.min_order_count = parseInt(form.min_order_count);
    if (form.min_total_spent) rules.min_total_spent = parseFloat(form.min_total_spent);
    try {
      await api.post("/raffles", {
        title: form.title.trim(),
        description: form.description,
        prize: form.prize,
        winner_count: parseInt(form.winner_count) || 1,
        rules,
      });
      toast.success("Çekiliş oluşturuldu");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Oluşturulamadı");
    }
  };

  return (
    <div className="bg-white border border-[#E8E2D5] rounded-2xl p-5 space-y-3">
      <h4 className="font-heading font-bold">Yeni Çekiliş</h4>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Başlık *</Label>
          <Input data-testid="raffle-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-[#FAF7F2]" placeholder="Yılbaşı Çekilişi" />
        </div>
        <div className="col-span-2">
          <Label>Açıklama</Label>
          <Textarea data-testid="raffle-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="bg-[#FAF7F2]" />
        </div>
        <div>
          <Label>Ödül</Label>
          <Input data-testid="raffle-prize" value={form.prize} onChange={(e) => set("prize", e.target.value)} className="bg-[#FAF7F2]" placeholder="500 TL alışveriş çeki" />
        </div>
        <div>
          <Label>Kazanan Sayısı</Label>
          <Input data-testid="raffle-winners" type="number" min="1" value={form.winner_count} onChange={(e) => set("winner_count", e.target.value)} className="bg-[#FAF7F2]" />
        </div>
        <div className="col-span-2 grid grid-cols-2 gap-3 bg-[#2E5A36]/5 p-3 rounded-xl">
          <p className="col-span-2 text-[10px] uppercase font-heading font-bold text-[#2E5A36]">
            Uygunluk Kuralları (opsiyonel — boş bırakılırsa tüm müşteriler)
          </p>
          <div>
            <Label>Son sipariş — başlangıç</Label>
            <Input data-testid="raffle-date-from" type="date" value={form.date_from} onChange={(e) => set("date_from", e.target.value)} className="bg-white" />
          </div>
          <div>
            <Label>Son sipariş — bitiş</Label>
            <Input data-testid="raffle-date-to" type="date" value={form.date_to} onChange={(e) => set("date_to", e.target.value)} className="bg-white" />
          </div>
          <div>
            <Label>Min sipariş sayısı</Label>
            <Input data-testid="raffle-min-orders" type="number" value={form.min_order_count} onChange={(e) => set("min_order_count", e.target.value)} className="bg-white" placeholder="3" />
          </div>
          <div>
            <Label>Min harcama (₺)</Label>
            <Input data-testid="raffle-min-spent" type="number" value={form.min_total_spent} onChange={(e) => set("min_total_spent", e.target.value)} className="bg-white" placeholder="500" />
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose} className="rounded-full">İptal</Button>
        <Button data-testid="raffle-save" onClick={save} className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full">Kaydet</Button>
      </div>
    </div>
  );
}

function RaffleCard({ raffle, customerCount, onChange }) {
  const [seed, setSeed] = useState("");
  const [busy, setBusy] = useState(false);

  const publicUrl = `${window.location.origin}/cekilis/${raffle.slug}`;

  const freeze = async () => {
    if (!window.confirm(`Uygun müşteriler dondurulacak (max ${customerCount}). Devam?`)) return;
    setBusy(true);
    try {
      await api.post(`/raffles/${raffle.id}/freeze`);
      toast.success("Katılımcılar donduruldu");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dondurulamadı");
    } finally {
      setBusy(false);
    }
  };

  const draw = async () => {
    if (!seed.trim()) return toast.error("Public seed girin (örn. Milli Piyango sonucu)");
    if (!window.confirm(`Seed: "${seed.trim()}" ile ${raffle.winner_count} kazanan çekilecek. Emin misiniz?`)) return;
    setBusy(true);
    try {
      await api.post(`/raffles/${raffle.id}/draw`, { seed: seed.trim() });
      toast.success("🎉 Çekiliş yapıldı!");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Çekilişte hata");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Çekiliş silinsin mi?")) return;
    try {
      await api.delete(`/raffles/${raffle.id}`);
      toast.success("Silindi");
      onChange();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Bağlantı kopyalandı");
  };

  const waLink = (num) => `https://wa.me/?text=${encodeURIComponent(`🎉 İkizler Market çekilişini kazandınız! Müşteri No: #${String(num).padStart(4, "0")} — ödülünüzü almak için lütfen bizimle iletişime geçin. Şeffaflık: ${publicUrl}`)}`;

  return (
    <div data-testid={`raffle-card-${raffle.id}`} className="bg-white border border-[#E8E2D5] rounded-2xl p-5 space-y-3">
      <div className="flex flex-wrap justify-between gap-2 items-start">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-heading font-bold text-lg">{raffle.title}</h4>
            <Badge
              className={
                raffle.status === "drawn"
                  ? "bg-[#2E5A36] text-[#FAF7F2]"
                  : raffle.status === "frozen"
                  ? "bg-[#E5A024] text-white"
                  : "bg-[#E8E2D5] text-[#1B2A22]"
              }
            >
              {raffle.status === "drawn" ? "Çekildi" : raffle.status === "frozen" ? "Dondu" : "Taslak"}
            </Badge>
          </div>
          {raffle.prize && <p className="text-sm text-[#D9653B] font-semibold">🎁 {raffle.prize}</p>}
          {raffle.description && <p className="text-xs text-[#1B2A22]/70 mt-1">{raffle.description}</p>}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={copyLink} data-testid={`raffle-copy-${raffle.id}`} className="rounded-full h-8" title="Bağlantıyı kopyala">
            <Copy className="w-3 h-3" />
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" className="rounded-full h-8" title="Herkese açık sayfa">
              <Eye className="w-3 h-3" />
            </Button>
          </a>
          <Button size="sm" variant="outline" onClick={del} data-testid={`raffle-del-${raffle.id}`} className="rounded-full h-8 text-red-600" title="Sil">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-[#FAF7F2] rounded-lg p-2">
          <p className="text-[10px] uppercase text-[#2E5A36] font-heading font-bold">Katılımcı</p>
          <p className="font-mono font-extrabold text-xl">{raffle.eligibles?.length || 0}</p>
        </div>
        <div className="bg-[#FAF7F2] rounded-lg p-2">
          <p className="text-[10px] uppercase text-[#2E5A36] font-heading font-bold">Kazanan</p>
          <p className="font-mono font-extrabold text-xl">{raffle.winner_count}</p>
        </div>
        <div className="bg-[#FAF7F2] rounded-lg p-2">
          <p className="text-[10px] uppercase text-[#2E5A36] font-heading font-bold">Durum</p>
          <p className="font-heading font-bold text-xs mt-1">{raffle.status}</p>
        </div>
      </div>

      {raffle.status === "draft" && (
        <Button data-testid={`raffle-freeze-${raffle.id}`} onClick={freeze} disabled={busy} className="w-full bg-[#E5A024] hover:bg-[#B4711C] text-white rounded-full font-heading font-bold">
          <Snowflake className="w-4 h-4 mr-2" /> Uygun Müşterileri Dondur
        </Button>
      )}

      {raffle.status === "frozen" && (
        <div className="space-y-2">
          <Label className="text-xs">Public Seed (Milli Piyango sonucu, random.org kodu vb.)</Label>
          <div className="flex gap-2">
            <Input
              data-testid={`raffle-seed-${raffle.id}`}
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="15-24-37-42-56-8"
              className="bg-[#FAF7F2] flex-1"
            />
            <Button
              data-testid={`raffle-draw-${raffle.id}`}
              onClick={draw}
              disabled={busy}
              className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
            >
              <Dice5 className="w-4 h-4 mr-1" /> Çek!
            </Button>
          </div>
          <p className="text-[10px] text-[#1B2A22]/60">
            💡 Seed'i çekiliş öncesinde sosyal medyada duyurup çekilişten sonra doğrulamayı gösterin.
          </p>
        </div>
      )}

      {raffle.status === "drawn" && (
        <>
          <div className="bg-gradient-to-r from-[#2E5A36] to-[#1B2A22] rounded-xl p-3 text-[#FAF7F2]">
            <p className="text-[10px] uppercase font-heading font-bold text-[#E5A024]">
              🏆 Kazananlar
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {(raffle.winners || []).map((w) => (
                <div key={w} className="bg-white/10 rounded-lg px-2 py-1 flex items-center gap-2" data-testid={`raffle-winner-${raffle.id}-${w}`}>
                  <span className="font-mono font-extrabold text-lg">#{String(w).padStart(4, "0")}</span>
                  <a href={waLink(w)} target="_blank" rel="noreferrer" className="text-xs bg-[#25D366] rounded-full px-2 py-0.5 font-bold">
                    WA
                  </a>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-[#1B2A22]/60 font-mono break-all">
            Seed: {raffle.seed}
          </p>
        </>
      )}
    </div>
  );
}

function CourierPanel({ couriers, onChange }) {
  const { t } = useApp();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [balances, setBalances] = useState([]);
  const [expanded, setExpanded] = useState(null); // courier_id with open details
  const [deliveries, setDeliveries] = useState({}); // {courier_id: {total, items}}

  const loadBalances = async () => {
    try {
      const r = await api.get("/admin/couriers/balance");
      setBalances(r.data || []);
    } catch {}
  };

  const loadDeliveries = async (cid) => {
    try {
      const r = await api.get(`/admin/couriers/${cid}/deliveries?limit=50`);
      setDeliveries((prev) => ({ ...prev, [cid]: r.data }));
    } catch {
      setDeliveries((prev) => ({ ...prev, [cid]: { total: 0, items: [] } }));
    }
  };

  const toggleExpand = (cid) => {
    if (expanded === cid) {
      setExpanded(null);
    } else {
      setExpanded(cid);
      if (!deliveries[cid]) loadDeliveries(cid);
    }
  };

  useEffect(() => {
    loadBalances();
    const i = setInterval(loadBalances, 30000);
    return () => clearInterval(i);
  }, [couriers.length]);

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/couriers", form);
      toast.success("Kurye eklendi");
      setForm({ name: "", phone: "" });
      onChange();
    } catch {
      toast.error("Eklenemedi");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Kurye silinsin mi?")) return;
    try {
      await api.delete(`/couriers/${id}`);
      onChange();
    } catch {}
  };
  const clearBalance = async (c) => {
    const bal = balances.find((b) => b.courier_id === c.id);
    if (!bal) return;
    if (!window.confirm(`${c.name}'den ₺${bal.balance.toFixed(2)} nakit teslim alındı mı? Bakiye sıfırlanacak.`)) return;
    try {
      await api.post(`/admin/couriers/${c.id}/clear-balance`);
      toast.success(`${c.name} bakiyesi kapatıldı`);
      loadBalances();
    } catch {
      toast.error("Kapatılamadı");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[160px]">
          <Label>İsim</Label>
          <Input
            data-testid="courier-name-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ahmet"
            className="bg-[#FAF7F2]"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label>Telefon (WhatsApp) *</Label>
          <Input
            data-testid="courier-phone-input"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+90 533 ..."
            className="bg-[#FAF7F2]"
          />
        </div>
        <Button
          data-testid="add-courier-btn"
          onClick={add}
          className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
        >
          <Plus className="w-4 h-4 mr-1" />
          {t("add")}
        </Button>
      </div>
      <p className="text-xs text-[#1B2A22]/60 px-1">
        💡 Telefon numarası WhatsApp'a bağlı olmalı — sipariş fişleri buradan gönderilir.
      </p>
      <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ad</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead className="text-right">Elindeki Nakit</TableHead>
              <TableHead className="text-right">Teslimat</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {couriers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6 text-[#1B2A22]/60">
                  Henüz kurye tanımlanmadı.
                </TableCell>
              </TableRow>
            )}
            {couriers.map((c, i) => {
              const bal = balances.find((b) => b.courier_id === c.id);
              const amount = bal?.balance || 0;
              const count = bal?.orders?.length || 0;
              const open = expanded === c.id;
              const det = deliveries[c.id];
              return (
              <React.Fragment key={c.id}>
              <TableRow data-testid={`courier-row-${c.id}`} className={open ? "bg-[#2E5A36]/5" : ""}>
                <TableCell className="font-semibold">
                  <button
                    type="button"
                    data-testid={`courier-expand-${c.id}`}
                    onClick={() => toggleExpand(c.id)}
                    className="flex items-center gap-1 hover:text-[#2E5A36] transition"
                    title="Teslim edilen siparişleri gör"
                  >
                    <span className="inline-block w-4 text-center font-mono text-[#D9653B]">
                      {open ? "▼" : "▶"}
                    </span>
                    Paket {i + 1} · {c.name}
                  </button>
                </TableCell>
                <TableCell className="font-mono text-sm">{c.phone || "—"}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${amount > 0 ? "text-[#D9653B]" : "text-gray-400"}`}>
                  ₺{amount.toFixed(2)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{count}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    {amount > 0 && (
                      <Button
                        data-testid={`clear-balance-${c.id}`}
                        size="sm"
                        onClick={() => clearBalance(c)}
                        className="rounded-full bg-[#2E5A36] hover:bg-[#1B2A22] text-white h-8 text-xs"
                      >
                        Ödeme Alındı
                      </Button>
                    )}
                    <Button
                      data-testid={`delete-courier-${c.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => del(c.id)}
                      className="rounded-full text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              {open && (
                <TableRow data-testid={`courier-details-${c.id}`}>
                  <TableCell colSpan={5} className="bg-[#FAF7F2] p-3">
                    {!det ? (
                      <p className="text-center text-sm text-[#1B2A22]/60 py-4">Yükleniyor...</p>
                    ) : det.items.length === 0 ? (
                      <p className="text-center text-sm text-[#1B2A22]/60 py-4">
                        Bu kurye henüz teslim yapmamış.
                      </p>
                    ) : (
                      <div>
                        <p className="text-xs font-heading font-bold text-[#2E5A36] mb-2">
                          📦 Son {det.items.length} teslim · (Toplam {det.total})
                        </p>
                        <div className="max-h-[320px] overflow-y-auto space-y-1">
                          {det.items.map((o) => {
                            const paid = !!o.payment_received_at;
                            const pm = o.payment_collected_method;
                            const pmLabel = pm === "cash" ? "💵 Nakit" : pm === "card" ? "💳 Kart" : pm === "transfer" ? "🏦 Havale" : pm === "partial" ? "⚡ Kısmi" : (pm || "—");
                            return (
                              <div key={o.order_number} className="bg-white border border-[#E8E2D5] rounded-lg px-3 py-2 flex items-center gap-3 text-xs">
                                <span className="font-mono font-bold text-[#2E5A36]">#{o.order_number}</span>
                                <span className="flex-1 truncate">{o.customer_name}</span>
                                <span className="text-[#1B2A22]/60 truncate hidden md:inline">{o.address}</span>
                                <span className="font-mono font-bold">₺{(o.total || 0).toFixed(2)}</span>
                                <span className="font-mono">{pmLabel}</span>
                                {pm === "partial" && (
                                  <span className="font-mono text-[10px] text-[#1B2A22]/60 hidden lg:inline">
                                    N:₺{(o.payment_cash_amount || 0).toFixed(0)} · K:₺{(o.payment_card_amount || 0).toFixed(0)} · H:₺{(o.payment_transfer_amount || 0).toFixed(0)}
                                  </span>
                                )}
                                {paid ? (
                                  <span className="text-[10px] bg-[#2E5A36] text-white rounded-full px-2 py-0.5">✓ Mutabık</span>
                                ) : (pm === "cash" || pm === "partial") ? (
                                  <span className="text-[10px] bg-[#D9653B] text-white rounded-full px-2 py-0.5">⏳ Nakit</span>
                                ) : (
                                  <span className="text-[10px] bg-[#2E5A36] text-white rounded-full px-2 py-0.5">✓ Alındı</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ReportPanel() {
  const { t } = useApp();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/daily?date=${date}`);
      setReport(data);
    } catch (e) {
      toast.error("Rapor alınamadı");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [date]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label>Tarih</Label>
          <Input
            data-testid="report-date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#FAF7F2]"
          />
        </div>
        <Button
          data-testid="close-day-btn"
          onClick={() => {
            if (window.confirm(`${date} günü için kasa mutabakatı yazdırma önizlemesi açılsın mı?`)) {
              window.print();
            }
          }}
          className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full"
        >
          <Save className="w-4 h-4 mr-1" />
          {t("close_day")}
        </Button>
      </div>

      {report && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Sipariş</p>
            <p className="font-mono font-extrabold text-2xl">{report.total_orders}</p>
            <p className="text-xs text-[#1B2A22]/60">Teslim: {report.delivered_orders}</p>
          </div>
          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Nakit</p>
            <p data-testid="report-cash" className="font-mono font-extrabold text-2xl text-[#E5A024]">
              ₺{report.cash_total.toFixed(2)}
            </p>
          </div>
          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Kredi Kartı</p>
            <p data-testid="report-card" className="font-mono font-extrabold text-2xl text-[#2E5A36]">
              ₺{report.card_total.toFixed(2)}
            </p>
          </div>
          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">Toplam</p>
            <p data-testid="report-total" className="font-mono font-extrabold text-2xl text-[#1B2A22]">
              ₺{report.delivered_total.toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {report && (
        <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kurye</TableHead>
                <TableHead>{t("packages")}</TableHead>
                <TableHead>{t("cash_collected")}</TableHead>
                <TableHead>{t("card_collected")}</TableHead>
                <TableHead>{t("daily_total")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.by_courier.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-[#1B2A22]/60">
                    {loading ? "Yükleniyor..." : "Bu tarihte teslim edilmiş sipariş yok."}
                  </TableCell>
                </TableRow>
              )}
              {report.by_courier.map((r, i) => (
                <TableRow key={i} data-testid={`courier-report-row-${i}`}>
                  <TableCell className="font-semibold">{r.courier_name}</TableCell>
                  <TableCell className="font-mono">{r.packages}</TableCell>
                  <TableCell className="font-mono text-[#E5A024]">₺{r.cash_total.toFixed(2)}</TableCell>
                  <TableCell className="font-mono text-[#2E5A36]">₺{r.card_total.toFixed(2)}</TableCell>
                  <TableCell className="font-mono font-bold">₺{r.total.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-[#1B2A22]/50 p-3 border-t border-[#E8E2D5]">{t("delivered_only")}</p>
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ settings, refresh }) {
  const { t } = useApp();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (settings) setForm({ ...settings });
  }, [settings]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await api.patch("/settings", {
        min_order: parseFloat(form.min_order),
        service_fee: parseFloat(form.service_fee),
        announcements: form.announcements,
        rules: form.rules,
        store_open_hour: parseInt(form.store_open_hour),
        store_close_hour: parseInt(form.store_close_hour),
        delivery_start_hour: parseInt(form.delivery_start_hour),
        delivery_end_hour: parseInt(form.delivery_end_hour),
        whatsapp_number: form.whatsapp_number,
        instagram_url: form.instagram_url,
        address: form.address,
        phone: form.phone,
      });
      toast.success("Ayarlar kaydedildi");
      refresh();
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  const editArr = (key, idx, value) => {
    const next = [...(form[key] || [])];
    next[idx] = value;
    set(key, next);
  };
  const addArr = (key) => set(key, [...(form[key] || []), ""]);
  const delArr = (key, idx) => set(key, form[key].filter((_, i) => i !== idx));

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-white border border-[#E8E2D5] rounded-2xl p-5 space-y-3">
        <h3 className="font-heading font-bold text-lg">Genel</h3>
        <div>
          <Label>Ücretsiz Servis Eşiği (₺)</Label>
          <Input
            data-testid="settings-min-order"
            type="number"
            value={form.min_order}
            onChange={(e) => set("min_order", e.target.value)}
            className="bg-[#FAF7F2]"
          />
          <p className="text-[10px] text-[#1B2A22]/60 mt-1">
            Bu tutarın altında sepete servis ücreti eklenir.
          </p>
        </div>
        <div>
          <Label>Servis Ücreti (₺)</Label>
          <Input
            data-testid="settings-service-fee"
            type="number"
            value={form.service_fee ?? 40}
            onChange={(e) => set("service_fee", e.target.value)}
            className="bg-[#FAF7F2]"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Market Açılış (saat)</Label>
            <Input data-testid="settings-store-open" type="number" min="0" max="24" value={form.store_open_hour} onChange={(e) => set("store_open_hour", e.target.value)} className="bg-[#FAF7F2]" />
          </div>
          <div>
            <Label>Market Kapanış (saat)</Label>
            <Input data-testid="settings-store-close" type="number" min="0" max="24" value={form.store_close_hour} onChange={(e) => set("store_close_hour", e.target.value)} className="bg-[#FAF7F2]" />
          </div>
          <div>
            <Label>Paket Servis Başlangıç</Label>
            <Input data-testid="settings-delivery-start" type="number" min="0" max="24" value={form.delivery_start_hour} onChange={(e) => set("delivery_start_hour", e.target.value)} className="bg-[#FAF7F2]" />
          </div>
          <div>
            <Label>Paket Servis Bitiş</Label>
            <Input data-testid="settings-delivery-end" type="number" min="0" max="24" value={form.delivery_end_hour} onChange={(e) => set("delivery_end_hour", e.target.value)} className="bg-[#FAF7F2]" />
          </div>
        </div>
        <div>
          <Label>WhatsApp Numarası (kod dahil, +'sız)</Label>
          <Input data-testid="settings-whatsapp" value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} className="bg-[#FAF7F2]" placeholder="905391095756" />
        </div>
        <div>
          <Label>Instagram URL</Label>
          <Input data-testid="settings-instagram" value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} className="bg-[#FAF7F2]" />
        </div>
        <div>
          <Label>Adres</Label>
          <Input data-testid="settings-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-[#FAF7F2]" />
        </div>
        <div>
          <Label>Telefon</Label>
          <Input data-testid="settings-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#FAF7F2]" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-heading font-bold text-lg">{t("announcements")}</h3>
            <Button data-testid="add-announcement" size="sm" onClick={() => addArr("announcements")} className="rounded-full bg-[#2E5A36]">
              <Plus className="w-3 h-3 mr-1" /> Ekle
            </Button>
          </div>
          <div className="space-y-2">
            {(form.announcements || []).map((a, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`announcement-${i}`} value={a} onChange={(e) => editArr("announcements", i, e.target.value)} className="bg-[#FAF7F2]" />
                <Button size="sm" variant="outline" onClick={() => delArr("announcements", i)} className="text-red-600 rounded-full">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-heading font-bold text-lg">{t("rules")}</h3>
            <Button data-testid="add-rule" size="sm" onClick={() => addArr("rules")} className="rounded-full bg-[#2E5A36]">
              <Plus className="w-3 h-3 mr-1" /> Ekle
            </Button>
          </div>
          <div className="space-y-2">
            {(form.rules || []).map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`rule-${i}`} value={r} onChange={(e) => editArr("rules", i, e.target.value)} className="bg-[#FAF7F2]" />
                <Button size="sm" variant="outline" onClick={() => delArr("rules", i)} className="text-red-600 rounded-full">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <Button
          data-testid="save-settings-btn"
          onClick={save}
          className="w-full h-12 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
        >
          <Save className="w-4 h-4 mr-2" />
          Ayarları Kaydet
        </Button>
      </div>

      {/* SPONSOR BANNERS — bağımsız kaydeder, ana ayarlardan ayrı */}
      <div className="lg:col-span-2">
        <SponsorBannersPanel settings={settings} onSaved={refresh} />
      </div>
    </div>
  );
}

function ProductFormDialog({ open, product, categories, onClose, onSaved }) {
  const { t } = useApp();
  const [form, setForm] = useState({});

  useEffect(() => {
    if (product) setForm(product);
    else
      setForm({
        name_tr: "", name_en: "", description_tr: "", description_en: "",
        price: 0, old_price: null, unit: "adet",
        sell_by: "unit", min_grams: 250, step_grams: 50, default_grams: 500,
        category_slug: categories[0]?.slug || "", image_url: "",
        stock: 100, brand: "", barcode: "", akinsoft_code: "", akinsoft_synced: true,
        featured: false, is_campaign: false, is_weekly_deal: false,
      });
  }, [product, open, categories]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        old_price: form.old_price ? parseFloat(form.old_price) : null,
        min_grams: parseInt(form.min_grams) || 250,
        step_grams: parseInt(form.step_grams) || 50,
        default_grams: parseInt(form.default_grams) || 500,
      };
      if (product?.id) await api.patch(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="product-form-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{product ? t("edit") : t("add_product")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t("name_tr")}</Label>
            <Input data-testid="pf-name-tr" value={form.name_tr || ""} onChange={(e) => set("name_tr", e.target.value)} className="bg-white" />
          </div>
          <div>
            <Label>{t("name_en")}</Label>
            <Input data-testid="pf-name-en" value={form.name_en || ""} onChange={(e) => set("name_en", e.target.value)} className="bg-white" />
          </div>
          <div>
            <Label>{t("price")} (₺)</Label>
            <Input data-testid="pf-price" type="number" step="0.01" value={form.price || 0} onChange={(e) => set("price", e.target.value)} className="bg-white" />
            <p className="text-[10px] text-[#1B2A22]/60 mt-1">
              {form.sell_by === "kg" ? "Bir kilogram fiyatı" : "Birim fiyatı"}
            </p>
          </div>
          <div>
            <Label>Eski Fiyat (₺, opsiyonel)</Label>
            <Input data-testid="pf-old-price" type="number" step="0.01" value={form.old_price ?? ""} onChange={(e) => set("old_price", e.target.value)} className="bg-white" />
          </div>
          <div className="col-span-2">
            <Label>Satış Birimi</Label>
            <Select value={form.sell_by || "unit"} onValueChange={(v) => set("sell_by", v)}>
              <SelectTrigger data-testid="pf-sell-by" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unit">Adet (parça)</SelectItem>
                <SelectItem value="kg">Kilogram (gram bazlı satış)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.sell_by === "kg" && (
            <div className="col-span-2 grid grid-cols-3 gap-3 bg-[#2E5A36]/5 border border-[#2E5A36]/20 rounded-xl p-3">
              <div>
                <Label>Min. Gram</Label>
                <Input data-testid="pf-min-grams" type="number" value={form.min_grams || 250} onChange={(e) => set("min_grams", e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label>Adım (gr)</Label>
                <Input data-testid="pf-step-grams" type="number" value={form.step_grams || 50} onChange={(e) => set("step_grams", e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label>Varsayılan (gr)</Label>
                <Input data-testid="pf-default-grams" type="number" value={form.default_grams || 500} onChange={(e) => set("default_grams", e.target.value)} className="bg-white" />
              </div>
            </div>
          )}
          <div>
            <Label>{t("unit")}</Label>
            <Input data-testid="pf-unit" value={form.unit || ""} onChange={(e) => set("unit", e.target.value)} className="bg-white" placeholder={form.sell_by === "kg" ? "kg" : "adet"} />
          </div>
          <div>
            <Label>{t("stock")}</Label>
            <Input data-testid="pf-stock" type="number" value={form.stock || 0} onChange={(e) => set("stock", e.target.value)} className="bg-white" />
          </div>
          <div className="col-span-2">
            <Label>{t("category")}</Label>
            <Select value={form.category_slug || ""} onValueChange={(v) => set("category_slug", v)}>
              <SelectTrigger data-testid="pf-category" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name_tr}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>{t("image_url")}</Label>
            <Input data-testid="pf-image" value={form.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="bg-white" />
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="mt-2 w-20 h-20 rounded-lg object-cover" />
            )}
          </div>
          <div>
            <Label>Marka</Label>
            <Input data-testid="pf-brand" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} className="bg-white" placeholder="Örn: Pınar, Nestle" />
          </div>
          <div>
            <Label>Barkod</Label>
            <Input data-testid="pf-barcode" value={form.barcode || ""} onChange={(e) => set("barcode", e.target.value)} className="bg-white" placeholder="EAN-13" />
          </div>
          <div className="col-span-2">
            <Label>{t("akinsoft_code")}</Label>
            <Input data-testid="pf-akinsoft" value={form.akinsoft_code || ""} onChange={(e) => set("akinsoft_code", e.target.value)} className="bg-white" />
          </div>
          <div className="col-span-2 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pf-featured" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} />
              {t("featured")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pf-is-campaign" checked={!!form.is_campaign} onChange={(e) => set("is_campaign", e.target.checked)} />
              Kampanya
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pf-is-weekly" checked={!!form.is_weekly_deal} onChange={(e) => set("is_weekly_deal", e.target.checked)} />
              Haftanın İndirimi
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pf-akinsoft-synced" checked={!!form.akinsoft_synced} onChange={(e) => set("akinsoft_synced", e.target.checked)} />
              Akınsoft Sync
            </label>
          </div>
          <div className="col-span-2">
            <Label>{t("description")} (TR)</Label>
            <Textarea data-testid="pf-desc-tr" value={form.description_tr || ""} onChange={(e) => set("description_tr", e.target.value)} rows={2} className="bg-white" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="pf-save" onClick={save} className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full">
            {t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ CATEGORIES PANEL (CRUD) ============
function CategoriesPanel({ categories, products, categoryCounts = {}, onChanged }) {
  const [editing, setEditing] = useState(null); // {slug, name_tr, name_en, order, is_hidden}
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug: "", name_tr: "", name_en: "", order: 0, is_hidden: false });

  // ---- MERGE (birleştirme) state ----
  const [selectedSlugs, setSelectedSlugs] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeMode, setMergeMode] = useState("existing"); // "existing" | "new"
  const [mergeTargetSlug, setMergeTargetSlug] = useState("");
  const [mergeNewName, setMergeNewName] = useState("");
  const [mergeNewIcon, setMergeNewIcon] = useState("");
  const [mergeDeleteSources, setMergeDeleteSources] = useState(true);
  const [mergeBusy, setMergeBusy] = useState(false);

  const toggleSel = (slug) => {
    setSelectedSlugs((prev) => {
      const n = new Set(prev);
      if (n.has(slug)) n.delete(slug); else n.add(slug);
      return n;
    });
  };
  const clearSel = () => setSelectedSlugs(new Set());

  const openMerge = () => {
    const list = Array.from(selectedSlugs);
    if (list.length < 2) {
      toast.error("En az 2 kategori seç");
      return;
    }
    // Default: ilk seçili kategori hedef
    setMergeMode("existing");
    setMergeTargetSlug(list[0]);
    setMergeNewName("");
    setMergeNewIcon("");
    setMergeDeleteSources(true);
    setMergeOpen(true);
  };

  const slugifyLocal = (s) => (s || "").toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const doMerge = async () => {
    const sources = Array.from(selectedSlugs);
    let targetSlug = "";
    let payload = { source_slugs: sources, delete_sources: mergeDeleteSources };
    if (mergeMode === "existing") {
      if (!mergeTargetSlug) { toast.error("Hedef kategori seç"); return; }
      targetSlug = mergeTargetSlug;
      payload.target_slug = targetSlug;
      // Yeni isim verildiyse rename
      if (mergeNewName.trim()) {
        payload.target_name_tr = mergeNewName.trim();
        payload.target_name_en = mergeNewName.trim();
      }
      if (mergeNewIcon.trim()) payload.target_icon = mergeNewIcon.trim();
    } else {
      // Yeni kategori oluştur
      const name = mergeNewName.trim();
      if (!name) { toast.error("Yeni kategori adı zorunlu"); return; }
      targetSlug = slugifyLocal(name);
      if (!targetSlug) { toast.error("Geçerli bir isim gir"); return; }
      payload.target_slug = targetSlug;
      payload.target_name_tr = name;
      payload.target_name_en = name;
      if (mergeNewIcon.trim()) payload.target_icon = mergeNewIcon.trim();
    }
    setMergeBusy(true);
    try {
      const { data } = await api.post("/categories/merge", payload);
      toast.success(`✓ ${data.moved_products} ürün "${targetSlug}" içine taşındı · ${data.deleted_sources} kaynak silindi`);
      clearSel();
      setMergeOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Birleştirme başarısız");
    } finally {
      setMergeBusy(false);
    }
  };

  const openNew = () => {
    setForm({ slug: "", name_tr: "", name_en: "", order: categories.length, is_hidden: false });
    setCreating(true);
  };
  const openEdit = (c) => {
    setForm({
      slug: c.slug,
      name_tr: c.name_tr || "",
      name_en: c.name_en || "",
      order: c.order ?? 0,
      is_hidden: c.is_hidden || false,
    });
    setEditing(c);
  };
  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const slugify = (s) => (s || "").toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const save = async () => {
    try {
      if (creating) {
        const slug = form.slug.trim() || slugify(form.name_tr);
        if (!slug || !form.name_tr.trim()) {
          toast.error("Kategori adı zorunlu");
          return;
        }
        await api.post("/categories", {
          slug,
          name_tr: form.name_tr.trim(),
          name_en: form.name_en.trim() || form.name_tr.trim(),
          order: parseInt(form.order) || 0,
          is_hidden: !!form.is_hidden,
        });
        toast.success("Kategori eklendi");
      } else if (editing) {
        await api.patch(`/categories/${editing.slug}`, {
          name_tr: form.name_tr.trim(),
          name_en: form.name_en.trim() || form.name_tr.trim(),
          order: parseInt(form.order) || 0,
          is_hidden: !!form.is_hidden,
        });
        toast.success("Kategori güncellendi");
      }
      close();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const remove = async (c) => {
    // Backend-side gerçek sayı (local state inactive/fiyat=0 ürünleri gizliyor)
    const localCount = products.filter((p) => p.category_slug === c.slug).length;
    const realCount = categoryCounts[c.slug] ?? localCount;
    if (realCount > 0) {
      const choice = window.confirm(
        `"${c.name_tr}" kategorisinde ${realCount} ürün var.\n\n` +
        `✓ OK: Ürünleri "Yeni Ürünler" kategorisine taşıyıp bu kategoriyi SİL\n` +
        `✗ Vazgeç: İptal (dilersen önce "Gizle" kullan)`
      );
      if (!choice) return;
      try {
        const { data } = await api.delete(`/categories/${c.slug}?reassign_to=yeni-urunler`);
        toast.success(`✓ ${data.moved || 0} ürün Yeni Ürünler'e taşındı · kategori silindi`);
        onChanged();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Silinemedi");
      }
      return;
    }
    if (!window.confirm(`"${c.name_tr}" kategorisini silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/categories/${c.slug}`);
      toast.success("Kategori silindi");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const toggleHide = async (c) => {
    try {
      await api.patch(`/categories/${c.slug}`, { is_hidden: !c.is_hidden });
      toast.success(c.is_hidden ? "Kategori gösteriliyor" : "Kategori gizlendi");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const prodCount = (slug) => categoryCounts[slug] ?? products.filter((p) => p.category_slug === slug).length;

  return (
    <div className="space-y-4">
      <div className="bg-[#2E5A36]/5 border border-[#2E5A36]/20 rounded-2xl p-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#1B2A22] mb-1 flex items-center gap-2">
            <FolderTree className="w-5 h-5" /> Kategori Yönetimi
          </h3>
          <p className="text-xs text-[#1B2A22]/70">
            Ekle, düzenle, gizle veya sil. ERP senkronundan gelen yeni kategoriler otomatik oluşur ama buradan düzenleyebilirsin.
            <br />
            <b>Not:</b> Ürünü olan kategori silinemez — önce "Gizle" seçeneğini kullan ya da ürünleri taşı.
          </p>
        </div>
        <Button
          data-testid="cat-add-btn"
          onClick={openNew}
          className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full shrink-0"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Kategori
        </Button>
      </div>

      {/* Seçim/birleştirme toolbar */}
      {selectedSlugs.size > 0 && (
        <div className="bg-[#2E5A36]/5 border border-[#2E5A36]/30 rounded-2xl p-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-heading font-bold text-[#2E5A36]">
            {selectedSlugs.size} kategori seçildi
          </span>
          <Button
            data-testid="cat-merge-btn"
            onClick={openMerge}
            disabled={selectedSlugs.size < 2}
            className="bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full"
            title="Seçili kategorileri tek bir hedefte birleştir (ürünler taşınır, kaynaklar silinir)"
          >
            🔗 Birleştir ({selectedSlugs.size})
          </Button>
          <Button
            data-testid="cat-sel-clear-btn"
            onClick={clearSel}
            variant="outline"
            className="rounded-full"
          >
            Seçimi Temizle
          </Button>
          <span className="text-[11px] text-[#1B2A22]/60 ml-2">
            💡 İpucu: Benzer kategorileri birleştirip tek bir isim verebilirsiniz
          </span>
        </div>
      )}

      <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="w-16">Sıra</TableHead>
              <TableHead>Türkçe Adı</TableHead>
              <TableHead>İngilizce</TableHead>
              <TableHead className="font-mono">Slug</TableHead>
              <TableHead className="text-right">Ürün</TableHead>
              <TableHead className="text-center">Durum</TableHead>
              <TableHead className="text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...categories].sort((a, b) => (a.order || 0) - (b.order || 0)).map((c) => (
              <TableRow key={c.slug} data-testid={`cat-row-${c.slug}`} className={c.is_hidden ? "bg-gray-50 text-gray-400" : ""}>
                <TableCell>
                  <input
                    type="checkbox"
                    data-testid={`cat-select-${c.slug}`}
                    checked={selectedSlugs.has(c.slug)}
                    onChange={() => toggleSel(c.slug)}
                    className="w-4 h-4 accent-[#2E5A36]"
                    title="Birleştirme için seç"
                  />
                </TableCell>
                <TableCell className="font-mono">{c.order ?? "—"}</TableCell>
                <TableCell className="font-semibold">
                  {c.slug === "yeni-urunler" && <span className="mr-1" title="Kategori bekleyen ürünler buraya düşer">🆕</span>}
                  {c.name_tr}
                </TableCell>
                <TableCell className="text-sm text-[#1B2A22]/70">{c.name_en || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{c.slug}</TableCell>
                <TableCell className="text-right font-mono">{prodCount(c.slug)}</TableCell>
                <TableCell className="text-center">
                  {c.is_hidden ? (
                    <Badge variant="outline" className="text-gray-500 border-gray-300">Gizli</Badge>
                  ) : (
                    <Badge className="bg-[#2E5A36] text-white">Aktif</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleHide(c)}
                      className="rounded-full h-8 text-xs"
                      data-testid={`cat-toggle-${c.slug}`}
                    >
                      {c.is_hidden ? "Göster" : "Gizle"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(c)}
                      className="rounded-full h-8 w-8 p-0"
                      data-testid={`cat-edit-${c.slug}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(c)}
                      className="rounded-full h-8 w-8 p-0 text-red-600"
                      data-testid={`cat-del-${c.slug}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#1B2A22]/50 py-6 text-sm">
                  Henüz kategori yok — "Yeni Kategori" ile ekle
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* MERGE Dialog — birleştirme */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { if (!o && !mergeBusy) setMergeOpen(false); }}>
        <DialogContent data-testid="cat-merge-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              🔗 Kategori Birleştirme
            </DialogTitle>
            <DialogDescription>
              Seçili <b>{selectedSlugs.size}</b> kategori tek bir hedefte birleştirilecek — tüm ürünler hedefe taşınır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
              <div className="text-xs font-heading font-bold text-[#2E5A36] mb-1">Kaynak kategoriler (taşınacak):</div>
              <div className="flex flex-wrap gap-1">
                {Array.from(selectedSlugs).map((s) => {
                  const c = categories.find((x) => x.slug === s);
                  const n = products.filter((p) => p.category_slug === s).length;
                  return (
                    <Badge key={s} variant="outline" className="text-xs">
                      {c?.icon || "📦"} {c?.name_tr || s} <span className="text-[#1B2A22]/50 ml-1">({n})</span>
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Hedef modu */}
            <div className="space-y-2">
              <Label className="text-xs font-heading font-bold">Hedef kategori</Label>
              <div className="flex gap-2">
                <label className="flex-1 flex items-center gap-2 p-2 border border-[#E8E2D5] rounded-xl bg-white cursor-pointer">
                  <input
                    type="radio"
                    name="merge-mode"
                    data-testid="cat-merge-mode-existing"
                    checked={mergeMode === "existing"}
                    onChange={() => setMergeMode("existing")}
                  />
                  <span className="text-sm">Mevcut bir kategoriye birleştir</span>
                </label>
                <label className="flex-1 flex items-center gap-2 p-2 border border-[#E8E2D5] rounded-xl bg-white cursor-pointer">
                  <input
                    type="radio"
                    name="merge-mode"
                    data-testid="cat-merge-mode-new"
                    checked={mergeMode === "new"}
                    onChange={() => setMergeMode("new")}
                  />
                  <span className="text-sm">Yeni bir kategori oluştur</span>
                </label>
              </div>
            </div>

            {mergeMode === "existing" ? (
              <div className="space-y-2">
                <Label className="text-xs">Hedef (kaynaklar buna taşınır)</Label>
                <Select value={mergeTargetSlug} onValueChange={setMergeTargetSlug}>
                  <SelectTrigger data-testid="cat-merge-target-select" className="bg-white">
                    <SelectValue placeholder="Hedef kategori seç..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(selectedSlugs).map((s) => {
                      const c = categories.find((x) => x.slug === s);
                      return (
                        <SelectItem key={s} value={s}>
                          {c?.icon || "📦"} {c?.name_tr || s} (hedef olacak)
                        </SelectItem>
                      );
                    })}
                    {categories.filter((c) => !selectedSlugs.has(c.slug)).map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>
                        {c.icon || "📦"} {c.name_tr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div>
                  <Label className="text-xs">Hedefi yeniden adlandır (opsiyonel)</Label>
                  <Input
                    data-testid="cat-merge-rename-input"
                    value={mergeNewName}
                    onChange={(e) => setMergeNewName(e.target.value)}
                    placeholder="Boş bırak → mevcut ad korunur"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs">İkon (opsiyonel, emoji)</Label>
                  <Input
                    data-testid="cat-merge-icon-input"
                    value={mergeNewIcon}
                    onChange={(e) => setMergeNewIcon(e.target.value)}
                    placeholder="Örn: 🥬"
                    className="bg-white"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs">Yeni kategori adı *</Label>
                <Input
                  data-testid="cat-merge-new-name"
                  value={mergeNewName}
                  onChange={(e) => setMergeNewName(e.target.value)}
                  placeholder="Örn: İçecekler & Soğuk"
                  className="bg-white"
                  autoFocus
                />
                <div>
                  <Label className="text-xs">İkon (opsiyonel, emoji)</Label>
                  <Input
                    data-testid="cat-merge-new-icon"
                    value={mergeNewIcon}
                    onChange={(e) => setMergeNewIcon(e.target.value)}
                    placeholder="Örn: 🥤"
                    className="bg-white"
                  />
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer p-2 bg-amber-50 border border-amber-200 rounded-xl">
              <input
                type="checkbox"
                data-testid="cat-merge-delete-sources"
                checked={mergeDeleteSources}
                onChange={(e) => setMergeDeleteSources(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">
                Ürünler taşındıktan sonra <b>kaynak kategorileri sil</b>
                <span className="block text-[11px] text-[#1B2A22]/60">
                  Kapalı: kaynaklar boş kategori olarak kalır (sitede gözükmez)
                </span>
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={mergeBusy}>İptal</Button>
            <Button
              data-testid="cat-merge-confirm-btn"
              onClick={doMerge}
              disabled={mergeBusy || selectedSlugs.size < 2}
              className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
            >
              {mergeBusy ? "Birleştiriliyor..." : `🔗 ${selectedSlugs.size} kategoriyi birleştir`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={creating || !!editing} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent data-testid="cat-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md" onEscapeKeyDown={close}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {creating ? "Yeni Kategori" : "Kategori Düzenle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Türkçe Adı *</Label>
              <Input
                data-testid="cat-name-tr"
                value={form.name_tr}
                onChange={(e) => setForm({ ...form, name_tr: e.target.value })}
                placeholder="Örn: Meyve & Sebze"
                className="bg-white"
              />
            </div>
            <div>
              <Label>İngilizce Adı (opsiyonel)</Label>
              <Input
                data-testid="cat-name-en"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                placeholder="Fruit & Vegetable"
                className="bg-white"
              />
            </div>
            <div>
              <Label>Slug (URL kısaltması) {creating && <span className="text-xs text-[#1B2A22]/60">— boş bırak otomatik oluşur</span>}</Label>
              <Input
                data-testid="cat-slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="meyve-sebze"
                className="bg-white font-mono"
                disabled={!!editing}
              />
              {editing && <p className="text-[11px] text-[#1B2A22]/50 mt-1">Slug sabit, değiştirilemez (ürün bağlantılarını bozmamak için)</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sıralama</Label>
                <Input
                  type="number"
                  data-testid="cat-order"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="bg-white"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="cat-hidden"
                    checked={form.is_hidden}
                    onChange={(e) => setForm({ ...form, is_hidden: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Gizli (sitede gösterme)</span>
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={close}>İptal</Button>
            <Button data-testid="cat-save" onClick={save} className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full">
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============ STOCK COUNT PANEL (barkod oku → sayılan gir → Excel indir) ============
const SCOUNT_STORAGE_KEY = "ikizler_stock_count_v1";

function StockCountPanel() {
  const { user } = useApp();
  const [excelOpen, setExcelOpen] = useState(false);
  // Restore session from localStorage on mount
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(SCOUNT_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.items) ? parsed.items : [];
    } catch {
      return [];
    }
  });
  const [depo, setDepo] = useState(() => {
    try {
      const raw = localStorage.getItem(SCOUNT_STORAGE_KEY);
      return (raw && JSON.parse(raw)?.depo) || "MERKEZ";
    } catch {
      return "MERKEZ";
    }
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false); // Barkodsuz ürün arama
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // Aynı barkod tekrar okutulduğunda açılan hızlı ekleme dialogu
  const [quickAdd, setQuickAdd] = useState(null); // null | {name, barcode, akinsoft_code, mevcut, currentCount}

  // === SHARED POOL (primary admin görüşü — tüm personelin canlı sayımı) ===
  const [showPool, setShowPool] = useState(false);
  const [pool, setPool] = useState({ items: [], operators: [], total: 0, can_see_all: false });
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolBusy, setPoolBusy] = useState(false);
  const [opFilter, setOpFilter] = useState("");  // "" = hepsi

  const fetchPool = async () => {
    setPoolLoading(true);
    try {
      const r = await api.get("/admin/stock-count/entries?all_operators=true");
      setPool(r.data || { items: [], operators: [], total: 0, can_see_all: false });
    } catch (e) {
      // 403 = yetkisi yok — paneli göstermeyeceğiz
      setPool({ items: [], operators: [], total: 0, can_see_all: false });
    } finally {
      setPoolLoading(false);
    }
  };

  // İlk mount'ta ve 15 sn'de bir sessizce yenile (yalnız panel açıksa)
  useEffect(() => {
    // Access check for the "Tüm Personel" button — try once silently
    fetchPool();
  }, []);
  useEffect(() => {
    if (!showPool) return;
    const id = setInterval(fetchPool, 15000);
    return () => clearInterval(id);
  }, [showPool]);

  const downloadSharedExcel = async (format = "wolvox") => {
    try {
      setPoolBusy(true);
      const res = await api.post(
        `/admin/stock-count/export-shared?all_operators=true&format=${format}`,
        {},
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = format === "wolvox"
        ? `sayim-wolvox-HAVUZ-${stamp}.xlsx`
        : `sayim-arsiv-HAVUZ-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(format === "wolvox"
        ? "✓ Havuz Akınsoft Excel'i indirildi — tüm personelin birleşik sayımı"
        : "✓ Havuz arşiv Excel'i indirildi (operatör + tarih dahil)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel oluşturulamadı");
    } finally {
      setPoolBusy(false);
    }
  };

  const clearPool = async (operator_email = null) => {
    const label = operator_email ? `${operator_email} kaydı` : "TÜM operatörlerin sayımları";
    if (!window.confirm(`⚠️ ${label} sunucudan kalıcı olarak silinecek. Devam?`)) return;
    try {
      const qs = operator_email
        ? `operator_email=${encodeURIComponent(operator_email)}`
        : "all_operators=true";
      const r = await api.delete(`/admin/stock-count/entries?${qs}`);
      toast.success(`✓ ${r.data?.deleted || 0} kayıt silindi`);
      await fetchPool();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  // Auto-save a single row to the backend (shared pool) so permanent admins
  // can see this operator's counts in real time and export them as one Excel.
  const saveEntry = async (i) => {
    const it = items[i];
    if (!it || (!it.akinsoft_code && !it.barcode)) return;
    setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, _saving: true, _saved: false } : x));
    try {
      await api.post("/admin/stock-count/entry", {
        akinsoft_code: it.akinsoft_code || null,
        barcode: it.barcode || null,
        name_tr: it.name_tr || "",
        mevcut: parseInt(it.mevcut || 0, 10),
        counted: parseFloat(it.counted || 0),
        depo_kodu: depo || "MERKEZ",
        unit: it.unit || "adet",
        sell_by: it.sell_by || "unit",
      });
      setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, _saving: false, _saved: true } : x));
    } catch (e) {
      setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, _saving: false, _saved: false } : x));
      toast.error(e?.response?.data?.detail || "Sunucuya kaydedilemedi");
    }
  };

  // Auto-save to localStorage on every change (debounced via rAF)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      try {
        localStorage.setItem(
          SCOUNT_STORAGE_KEY,
          JSON.stringify({ items, depo, updated_at: Date.now() })
        );
        setSavedAt(new Date());
      } catch {}
    });
    return () => cancelAnimationFrame(raf);
  }, [items, depo]);

  const lookupAndAdd = async (code) => {
    const trimmed = (code || "").trim();
    if (!trimmed) return;
    try {
      // Pasif + fiyatsız ürünleri de ara — sayım tamamlanana kadar pasiftekiler de sayılmalı
      const res = await api.get(
        `/products?search=${encodeURIComponent(trimmed)}&include_inactive=true&include_zero_price=true`
      );
      const products = res.data || [];
      const match = products.find(
        (p) => p.barcode === trimmed || p.akinsoft_code === trimmed
      );
      if (match) {
        const matchIsKg = match.sell_by === "kg" || (match.unit || "").toLowerCase() === "kg";
        const matchUnit = matchIsKg ? "kg" : "adet";
        const matchSellBy = matchIsKg ? "kg" : "unit";
        const defaultStep = matchIsKg ? 0.5 : 1;
        setItems((prev) => {
          const idx = prev.findIndex(
            (x) => x.barcode === match.barcode || x.akinsoft_code === match.akinsoft_code
          );
          if (idx >= 0) {
            // 🔁 Zaten listede — üste taşı ve hızlı ekleme dialogunu aç
            const copy = [...prev];
            const [row] = copy.splice(idx, 1);
            setQuickAdd({
              name: row.name_tr || match.name_tr,
              barcode: row.barcode || match.barcode || "",
              akinsoft_code: row.akinsoft_code || match.akinsoft_code || "",
              mevcut: row.mevcut ?? match.stock ?? 0,
              currentCount: parseFloat(row.counted) || 0,
              unit: row.unit || matchUnit,
              sell_by: row.sell_by || matchSellBy,
            });
            return [row, ...copy];
          }
          return [
            {
              barcode: match.barcode || "",
              akinsoft_code: match.akinsoft_code || "",
              name_tr: match.name_tr,
              mevcut: match.stock || 0,
              counted: defaultStep,
              status: "ok",
              unit: matchUnit,
              sell_by: matchSellBy,
            },
            ...prev,
          ];
        });
        toast.success(`${match.name_tr} +${defaultStep}${matchIsKg ? " kg" : ""}`);
        // Auto-save the new/updated entry to backend after a short tick so
        // the state write has flushed. Needed so primary admin sees it live.
        setTimeout(() => {
          setItems((prev) => {
            const idx2 = prev.findIndex(
              (x) => x.barcode === match.barcode || x.akinsoft_code === match.akinsoft_code
            );
            if (idx2 >= 0) saveEntry(idx2);
            return prev;
          });
        }, 50);
      } else {
        setItems((prev) => {
          // Yeni ürün değilse (SİSTEMDE BULUNAMADI listesindeyse) de tekrar okunduğunda hızlı ekle
          const idx = prev.findIndex((x) => x.barcode === trimmed);
          if (idx >= 0) {
            const copy = [...prev];
            const [row] = copy.splice(idx, 1);
            setQuickAdd({
              name: row.name_tr || "(SİSTEMDE BULUNAMADI)",
              barcode: trimmed,
              akinsoft_code: "",
              mevcut: 0,
              currentCount: parseFloat(row.counted) || 0,
            });
            return [row, ...copy];
          }
          toast.error(`${trimmed} sistemde yok — yine de listede`);
          return [
            {
              barcode: trimmed,
              akinsoft_code: "",
              name_tr: "(SİSTEMDE BULUNAMADI)",
              mevcut: 0,
              counted: 1,
              status: "missing",
            },
            ...prev,
          ];
        });
      }
    } catch (e) {
      toast.error("Arama hatası");
    }
  };

  const updateCount = (i, v) => {
    setItems((prev) => {
      const copy = [...prev];
      // Boş girişte ("" veya ".") 0 koruma — kullanıcı "." yazarken ara durumu bozma
      let next;
      if (v === "" || v === ".") next = 0;
      else next = parseFloat(v);
      if (!Number.isFinite(next)) next = 0;
      copy[i] = { ...copy[i], counted: next };
      return copy;
    });
  };
  const removeRow = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const clearAll = () => {
    if (!window.confirm("Tüm sayımı sil? (localStorage'dan da silinir)")) return;
    setItems([]);
    try { localStorage.removeItem(SCOUNT_STORAGE_KEY); } catch {}
  };

  const downloadExcel = async (format = "wolvox") => {
    if (items.length === 0) {
      toast.error("Önce ürün ekle");
      return;
    }
    try {
      setBusy(true);
      const payload = {
        depo_kodu: depo || "MERKEZ",
        items: items.map((it) => ({
          barcode: it.barcode || null,
          akinsoft_code: it.akinsoft_code || null,
          counted: it.counted,
        })),
      };
      const res = await api.post(`/admin/stock-count/export?format=${format}`, payload, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = format === "wolvox"
        ? `sayim-wolvox-${stamp}.xlsx`
        : `sayim-arsiv-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(format === "wolvox"
        ? "✓ Akınsoft formatı indirildi — Wolvox 'İçe Aktar' ile yükle"
        : "✓ Arşiv formatı indirildi — kişisel kayıt için");
    } catch (e) {
      toast.error("Excel oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  const totalItems = items.length;
  const totalCounted = items.reduce((s, it) => s + (parseFloat(it.counted) || 0), 0);
  const diffCount = items.filter(
    (it) => (parseFloat(it.counted) || 0) !== (parseFloat(it.mevcut) || 0)
  ).length;

  return (
    <div className="space-y-4">
      {/* SHARED POOL — primary admin canlı tüm personel görüşü */}
      {pool.can_see_all && (
        <div className="bg-gradient-to-br from-[#E5A024]/10 to-[#D9653B]/5 border-2 border-[#E5A024] rounded-2xl p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading text-base font-bold text-[#1B2A22] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#D9653B]" />
                👥 Personel Sayım Havuzu
                <span className="text-[10px] bg-[#D9653B] text-white rounded-full px-2 py-0.5 font-heading font-bold" data-testid="pool-total-badge">
                  {pool.total} satır · {pool.operators.length} operatör
                </span>
                {poolLoading && <span className="text-[10px] text-[#1B2A22]/50">yükleniyor...</span>}
              </h3>
              <p className="text-[11px] text-[#1B2A22]/70 mt-0.5">
                Haricen eklediğin personellerin okuttukları ürünler <b>canlı</b> olarak burada toplanır (15 sn'de otomatik yenilenir).
                Tek Excel'de topla, Wolvox'a yükle.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setShowPool((v) => !v); if (!showPool) fetchPool(); }}
                className={`rounded-full font-heading font-bold ${showPool ? "bg-[#D9653B] text-white border-[#D9653B]" : "border-[#D9653B] text-[#D9653B]"}`}
                data-testid="pool-toggle"
              >
                {showPool ? "▲ Kapat" : "👁 Havuzu Aç"}
              </Button>
              <DeactivateUncountedButton />
            </div>
          </div>

          {/* Operator breakdown chips */}
          {pool.operators.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="pool-operators">
              <button
                type="button"
                onClick={() => setOpFilter("")}
                className={`text-[11px] rounded-full px-3 py-1 font-heading font-bold border ${opFilter === "" ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5]"}`}
              >
                Hepsi ({pool.total})
              </button>
              {pool.operators.map((op) => (
                <button
                  key={op.operator_email}
                  type="button"
                  onClick={() => setOpFilter(op.operator_email)}
                  className={`text-[11px] rounded-full px-3 py-1 font-heading font-bold border ${opFilter === op.operator_email ? "bg-[#2E5A36] text-white border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5]"}`}
                  title={`${op.lines} satır · toplam ${op.total_counted.toFixed(2)}`}
                  data-testid={`pool-op-${op.operator_email}`}
                >
                  {op.operator_name} · {op.lines}
                </button>
              ))}
            </div>
          )}

          {showPool && (
            <>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E5A024]/30">
                <Button
                  size="sm"
                  onClick={() => downloadSharedExcel("wolvox")}
                  disabled={pool.total === 0 || poolBusy}
                  className="rounded-full bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold"
                  data-testid="pool-dl-wolvox"
                >
                  📊 Havuz · Akınsoft Excel ({pool.total})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadSharedExcel("admin")}
                  disabled={pool.total === 0 || poolBusy}
                  className="rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
                  data-testid="pool-dl-admin"
                >
                  📄 Havuz · Arşiv (operatör + tarih)
                </Button>
                {opFilter && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => clearPool(opFilter)}
                    className="rounded-full text-red-600 border-red-300 ml-auto"
                    data-testid="pool-clear-op"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> {opFilter} temizle
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => clearPool(null)}
                  disabled={pool.total === 0}
                  className={`rounded-full text-red-600 border-red-300 ${opFilter ? "" : "ml-auto"}`}
                  data-testid="pool-clear-all"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> HEPSİNİ sil
                </Button>
              </div>

              <div className="bg-white border border-[#E8E2D5] rounded-xl overflow-x-auto max-h-[400px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead>Operatör</TableHead>
                      <TableHead>Ürün</TableHead>
                      <TableHead>Stokkodu</TableHead>
                      <TableHead>Barkod</TableHead>
                      <TableHead>Depo</TableHead>
                      <TableHead className="text-right">Mevcut</TableHead>
                      <TableHead className="text-right">Sayılan</TableHead>
                      <TableHead className="text-right">Fark</TableHead>
                      <TableHead>Zaman</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(opFilter ? pool.items.filter((x) => x.operator_email === opFilter) : pool.items).map((x, i) => {
                      const fark = (parseFloat(x.counted) || 0) - (parseFloat(x.mevcut) || 0);
                      return (
                        <TableRow key={x.id || i} data-testid={`pool-row-${i}`}>
                          <TableCell className="font-mono text-[11px] text-[#2E5A36] font-bold">
                            {x.operator_name || (x.operator_email || "").split("@")[0]}
                          </TableCell>
                          <TableCell className="text-xs">{x.name_tr || "—"}</TableCell>
                          <TableCell className="font-mono text-[11px]">{x.akinsoft_code || "—"}</TableCell>
                          <TableCell className="font-mono text-[11px]">{x.barcode || "—"}</TableCell>
                          <TableCell className="font-mono text-[11px]">{x.depo_kodu || "MERKEZ"}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{x.mevcut}</TableCell>
                          <TableCell className="text-right font-mono text-xs font-bold">{x.counted}</TableCell>
                          <TableCell className={`text-right font-mono text-xs font-bold ${fark === 0 ? "text-gray-500" : fark > 0 ? "text-green-600" : "text-red-600"}`}>
                            {fark > 0 ? "+" : ""}{fark.toFixed(2)}
                          </TableCell>
                          <TableCell className="font-mono text-[10px] text-[#1B2A22]/60">
                            {x.updated_at ? new Date(x.updated_at).toLocaleString("tr-TR") : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {pool.total === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-6 text-[#1B2A22]/50 text-sm">
                          Henüz personelden sayım gelmedi. Personel barkod okutmaya başladığında burada canlı görünecek.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-[#2E5A36]/5 border border-[#2E5A36]/20 rounded-2xl p-4">
        <h3 className="font-heading text-lg font-bold text-[#1B2A22] mb-1 flex items-center gap-2">
          <ScanLine className="w-5 h-5" /> Stok Sayımı — 2 Formatlı Excel İndir
        </h3>
        <p className="text-xs text-[#1B2A22]/70">
          Barkod oku → sayılan miktarı gir → 2 Excel indir:
          <br />
          <b>📊 Akınsoft Formatı</b> — sadece <code>STOKKODU, DEPO_KODU, SAYILAN_MIKTAR</code> (Wolvox 'Stok Sayım İçe Aktar' ile uyumlu).
          <br />
          <b>📄 Benim Arşivim</b> — tüm detay (ad, barkod, mevcut stok, fark, durum, zaman damgası) — kendi kayıtların için.
        </p>
        {savedAt && items.length > 0 && (
          <p className="text-[11px] text-[#2E5A36] mt-2 flex items-center gap-1">
            <Check className="w-3 h-3" /> Tarayıcıya otomatik kaydedildi · {savedAt.toLocaleTimeString("tr-TR")}
            <span className="text-[#1B2A22]/50"> — sayfayı yenilesen de kaybolmaz</span>
          </p>
        )}
      </div>

      {/* Excel'den Katalog Yükle — birincil admin'e özel, sayım bitince */}
      {pool.can_see_all && (
        <div className="bg-white border-2 border-dashed border-[#D9653B] rounded-2xl p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm text-[#1B2A22]">📥 Sayım bittikten sonra: Siteyi tam Excel listesine göre güncelle</p>
            <p className="text-[11px] text-[#1B2A22]/70">
              Excel'deki ürünler aktif, listede olmayan ürünler siteden gizlenir (DB'de kalır, Wolvox sonra geri getirebilir).
            </p>
          </div>
          <Button
            onClick={() => setExcelOpen(true)}
            className="rounded-full bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold"
            data-testid="open-excel-catalog-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel'den Katalog Yükle
          </Button>
        </div>
      )}

      <ExcelCatalogDialog
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        onApplied={async () => { toast.success("✓ Katalog güncellendi — ana sayfa artık güncel listeyi gösteriyor"); }}
      />

      {/* 🔁 Hızlı Ekleme Dialogu — aynı barkod tekrar okutulduğunda açılır */}
      <QuickAddDialog
        data={quickAdd}
        onClose={() => setQuickAdd(null)}
        onAdd={(addQty) => {
          if (!quickAdd || !(addQty > 0)) { setQuickAdd(null); return; }
          const newTotal = (quickAdd.currentCount || 0) + addQty;
          setItems((prev) => {
            const idx = prev.findIndex(
              (x) => (quickAdd.barcode && x.barcode === quickAdd.barcode) ||
                     (quickAdd.akinsoft_code && x.akinsoft_code === quickAdd.akinsoft_code)
            );
            if (idx < 0) return prev;
            const copy = [...prev];
            copy[idx] = { ...copy[idx], counted: newTotal };
            // Kaydet (idx 0 olmalı çünkü üste taşımıştık)
            setTimeout(() => saveEntry(idx), 30);
            return copy;
          });
          toast.success(`+${addQty} eklendi · toplam: ${newTotal}`);
          setQuickAdd(null);
        }}
      />

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          data-testid="scount-scan-btn"
          onClick={() => setScannerOpen(true)}
          className="bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full"
        >
          <ScanLine className="w-4 h-4 mr-1" /> Barkod Oku (+1)
        </Button>
        <Button
          data-testid="scount-search-btn"
          onClick={() => setSearchOpen(true)}
          variant="outline"
          className="rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
          title="Barkodsuz ürünler (manav tartı, açık ürün) için ad/kod ile ara"
        >
          🔍 Barkodsuz Ara
        </Button>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Depo:</Label>
          <Input
            data-testid="scount-depo"
            value={depo}
            onChange={(e) => setDepo(e.target.value)}
            className="bg-white w-32 h-9"
          />
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button
            data-testid="scount-clear-btn"
            onClick={clearAll}
            variant="outline"
            disabled={items.length === 0}
            className="rounded-full text-red-600 border-red-300"
          >
            Temizle
          </Button>
          <Button
            data-testid="scount-download-admin-btn"
            onClick={() => downloadExcel("admin")}
            disabled={items.length === 0 || busy}
            variant="outline"
            className="rounded-full border-[#2E5A36] text-[#2E5A36] hover:bg-[#2E5A36]/10 font-heading font-bold"
            title="Tüm detaylarla kişisel arşiv Excel'i — Akınsoft'a yükleme için değil"
          >
            {busy ? "..." : `📄 Arşivim (${items.length})`}
          </Button>
          <Button
            data-testid="scount-download-btn"
            onClick={() => downloadExcel("wolvox")}
            disabled={items.length === 0 || busy}
            className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
            title="Akınsoft Wolvox 'Stok Sayım İçe Aktar' için — minimal sütunlu"
          >
            {busy ? "Hazırlanıyor..." : `📊 Akınsoft Excel (${items.length})`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
          <div className="text-[#1B2A22]/60 text-xs">Satır Sayısı</div>
          <div className="font-mono font-bold text-lg">{totalItems}</div>
        </div>
        <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
          <div className="text-[#1B2A22]/60 text-xs">Toplam Sayılan</div>
          <div className="font-mono font-bold text-lg">{totalCounted.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
          <div className="text-[#1B2A22]/60 text-xs">Farklı Satır</div>
          <div className="font-mono font-bold text-lg text-[#D9653B]">{diffCount}</div>
        </div>
      </div>

      {/* Desktop: full table */}
      <div className="hidden md:block bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ürün</TableHead>
              <TableHead>Stokkodu</TableHead>
              <TableHead>Barkod</TableHead>
              <TableHead className="text-right">Mevcut</TableHead>
              <TableHead className="text-right">Sayılan</TableHead>
              <TableHead className="text-right">Fark</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#1B2A22]/50 py-6 text-sm">
                  Henüz ürün yok — "Barkod Oku" ile başla
                </TableCell>
              </TableRow>
            )}
            {items.map((it, i) => {
              const fark = (parseFloat(it.counted) || 0) - (parseFloat(it.mevcut) || 0);
              return (
                <TableRow key={i} data-testid={`scount-row-${i}`} className={it.status === "missing" ? "bg-red-50" : ""}>
                  <TableCell className="font-semibold">
                    {it.name_tr}
                    {(it.unit === "kg" || it.sell_by === "kg") && (
                      <span className="ml-2 text-[10px] bg-[#2E5A36] text-white rounded-full px-1.5 py-0.5 font-heading font-bold">KG</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{it.akinsoft_code || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{it.barcode || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{it.mevcut}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => updateCount(i, Math.max(0, (parseFloat(it.counted) || 0) - (it.unit === "kg" || it.sell_by === "kg" ? 0.1 : 1)))}
                        className="h-8 w-8 p-0 rounded-full"
                        data-testid={`scount-minus-${i}`}
                      >
                        −
                      </Button>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        autoComplete="off"
                        value={it.counted ?? ""}
                        onChange={(e) => {
                          const v = e.target.value.replace(",", ".");
                          if (v === "" || /^\d*\.?\d*$/.test(v)) updateCount(i, v);
                        }}
                        onFocus={(e) => e.target.select()}
                        style={{
                          width: 80,
                          height: 32,
                          fontSize: 14,
                          fontWeight: 700,
                          textAlign: "right",
                          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                          color: "#1B2A22",
                          WebkitTextFillColor: "#1B2A22",
                          background: "#FFFFFF",
                          border: "1px solid #E8E2D5",
                          borderRadius: 6,
                          padding: "0 8px",
                          WebkitAppearance: "none",
                          appearance: "none",
                          opacity: 1,
                          outline: "none",
                        }}
                        data-testid={`scount-counted-${i}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => updateCount(i, (parseFloat(it.counted) || 0) + (it.unit === "kg" || it.sell_by === "kg" ? 0.1 : 1))}
                        className="h-8 w-8 p-0 rounded-full"
                        data-testid={`scount-plus-${i}`}
                      >
                        +
                      </Button>
                      <span className="text-[10px] font-heading font-bold text-[#1B2A22]/60 w-6">
                        {it.unit === "kg" || it.sell_by === "kg" ? "kg" : "ad"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={`text-right font-mono font-bold ${fark === 0 ? "text-gray-500" : fark > 0 ? "text-green-600" : "text-red-600"}`}>
                    {fark > 0 ? "+" : ""}{fark.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => removeRow(i)} className="rounded-full text-red-600 h-7 w-7 p-0">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: card list — large counted input is always visible, no horizontal scroll */}
      <div className="md:hidden space-y-2">
        {items.length === 0 && (
          <div className="bg-white border border-[#E8E2D5] rounded-2xl py-8 text-center text-[#1B2A22]/50 text-sm">
            Henüz ürün yok — "Barkod Oku" ile başla
          </div>
        )}
        {items.map((it, i) => {
          const fark = (parseFloat(it.counted) || 0) - (parseFloat(it.mevcut) || 0);
          return (
            <div
              key={i}
              data-testid={`scount-card-${i}`}
              className={`bg-white border rounded-2xl p-3 space-y-2 ${it.status === "missing" ? "bg-red-50 border-red-300" : "border-[#E8E2D5]"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-[#1B2A22] text-sm leading-tight">
                    {it.name_tr}
                    {(it.unit === "kg" || it.sell_by === "kg") && (
                      <span className="ml-2 text-[10px] bg-[#2E5A36] text-white rounded-full px-2 py-0.5 font-heading font-bold align-middle">KG</span>
                    )}
                  </p>
                  <p className="font-mono text-[11px] text-[#1B2A22]/60 mt-0.5">
                    {it.akinsoft_code || "—"} · {it.barcode || "—"}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeRow(i)}
                  className="rounded-full text-red-600 h-8 w-8 p-0 shrink-0"
                  data-testid={`scount-remove-mobile-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <Label className="text-[10px] text-[#1B2A22]/60">Mevcut</Label>
                  <div className="font-mono font-bold text-base text-[#1B2A22]">
                    {it.mevcut}
                    <span className="text-[10px] text-[#1B2A22]/60 ml-1">
                      {(it.unit === "kg" || it.sell_by === "kg") ? "kg" : "ad"}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <Label className="text-[10px] text-[#1B2A22]/60">Fark</Label>
                  <div className={`font-mono font-bold text-base ${fark === 0 ? "text-gray-500" : fark > 0 ? "text-green-600" : "text-red-600"}`}>
                    {fark > 0 ? "+" : ""}{fark.toFixed(2)}
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-[#D9653B] font-bold">
                  SAYILAN {(it.unit === "kg" || it.sell_by === "kg") ? "(KG)" : ""}
                </Label>
                <div className="grid items-center gap-2 mt-1" style={{ gridTemplateColumns: "48px minmax(0,1fr) 48px" }}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => updateCount(i, Math.max(0, (parseFloat(it.counted) || 0) - (it.unit === "kg" || it.sell_by === "kg" ? 0.1 : 1)))}
                    className="h-12 w-12 p-0 rounded-xl shrink-0 border-2 border-[#D9653B]/40 text-[#D9653B] font-heading font-bold text-xl"
                    data-testid={`scount-minus-mobile-${i}`}
                  >
                    −
                  </Button>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={it.counted ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.replace(",", ".");
                      if (v === "" || /^\d*\.?\d*$/.test(v)) updateCount(i, v);
                    }}
                    onFocus={(e) => e.target.select()}
                    style={{
                      display: "block",
                      width: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      height: 48,
                      fontSize: 22,
                      fontWeight: 800,
                      lineHeight: "normal",
                      textAlign: "center",
                      fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                      color: "#1B2A22",
                      WebkitTextFillColor: "#1B2A22",
                      background: "#FFFFFF",
                      border: "2px solid #D9653B",
                      borderRadius: 12,
                      padding: "0 4px",
                      WebkitAppearance: "none",
                      appearance: "none",
                      opacity: 1,
                      caretColor: "#D9653B",
                      outline: "none",
                    }}
                    data-testid={`scount-counted-mobile-${i}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => updateCount(i, (parseFloat(it.counted) || 0) + (it.unit === "kg" || it.sell_by === "kg" ? 0.1 : 1))}
                    className="h-12 w-12 p-0 rounded-xl shrink-0 border-2 border-[#D9653B]/40 text-[#D9653B] font-heading font-bold text-xl"
                    data-testid={`scount-plus-mobile-${i}`}
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanned={(code) => {
          setScannerOpen(false);
          lookupAndAdd(code);
        }}
      />

      {/* 🔍 Barkodsuz ürün arama — manav tartı, açık ürün vs. */}
      <ProductSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={(p) => {
          // Seçilen ürünü lookupAndAdd ile aynı yoldan ekle
          // (barkod yoksa akinsoft_code ile, o da yoksa name_tr ile eşleştir)
          const code = p.barcode || p.akinsoft_code || p.name_tr;
          if (code) lookupAndAdd(code);
        }}
      />
    </div>
  );
}


function DeactivateUncountedButton() {
  const [preview, setPreview] = useState(null); // {to_deactivate, protected_counted, cutoff_used, sample}
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [addToBlacklist, setAddToBlacklist] = useState(false);  // DEFAULT KAPALI — Akınsoft re-aktifleştirebilsin

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/deactivate-uncounted", { preview: true });
      setPreview(data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    const confirmMsg =
      `${preview.to_deactivate} sayılmamış ürün PASİFLEŞTİRİLECEK.\n` +
      `Sayılan ${preview.protected_counted} ürün korunur.\n` +
      (addToBlacklist
        ? `⚠ Kara listeye de eklenecek — Akınsoft sync bir sonraki aktarımda bu kodları ATLAR.\n`
        : `✓ Kara listeye EKLENMEYECEK — Admin veya Akınsoft tekrar aktifleştirebilir.\n`) +
      `72 saat içinde "Geri Al" yapabilirsin.\n\nDevam?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/deactivate-uncounted", {
        preview: false,
        blacklist: addToBlacklist,
      });
      toast.success(data.message || `✓ ${data.deactivated} ürün pasifleştirildi`);
      setPreview(null);
      setOpen(false);
      // Yenile — ProductsPanel'de pasif rozetleri görünsün
      try { window.dispatchEvent(new CustomEvent("admin-force-reload")); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pasifleştirme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    const mins = parseInt(window.prompt("Son kaç dakika içindeki pasifleştirmeyi geri al? (max 4320 = 72 saat)", "60") || "0", 10);
    if (!mins || mins < 1) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/products/deactivate-uncounted/undo?minutes=${mins}`);
      toast.success(data.message || `✓ ${data.restored} ürün geri aktif`);
      try { window.dispatchEvent(new CustomEvent("admin-force-reload")); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri alma başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={runPreview}
        className="rounded-full border-red-500 text-red-600 font-heading font-bold hover:bg-red-50"
        data-testid="deactivate-uncounted-btn"
        title="Sayılmamış tüm ürünleri pasifleştir (silmez — is_active=false)"
      >
        ⚠ Sayılmayanları Pasifle
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={undo}
        className="rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold hover:bg-[#2E5A36]/10"
        data-testid="deactivate-uncounted-undo-btn"
        title="Son N dakikada pasifleştirilenleri geri aktifleştir"
      >
        ↶ Geri Al
      </Button>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="deactivate-uncounted-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              ⚠ Sayılmayan Ürünleri Pasifle — Önizleme
            </DialogTitle>
            <DialogDescription>
              Sayılan ürünler ve sayımdan sonra eklenen/güncellenen ürünler korunur. Diğerleri <b>is_active=false</b> olur. Ürün SİLİNMEZ, 72 saat içinde geri alınabilir.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <div className="text-[11px] font-heading text-red-600">PASİFLEŞTİRİLECEK</div>
                  <div className="text-2xl font-heading font-bold text-red-700">{preview.to_deactivate}</div>
                </div>
                <div className="bg-[#2E5A36]/10 border border-[#2E5A36]/30 rounded-xl p-3">
                  <div className="text-[11px] font-heading text-[#2E5A36]">KORUNACAK (sayılan)</div>
                  <div className="text-2xl font-heading font-bold text-[#2E5A36]">{preview.protected_counted}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#1B2A22]/70">
                <b>Koruma kesme tarihi:</b> {preview.cutoff_used || "— (count_locked yok, cutoff yok)"}
                <br/>Bu tarihten sonra oluşturulan/güncellenen/sync edilen ürünler korunur.
              </div>

              {/* Blacklist opsiyonu — default kapalı */}
              <label className="flex items-start gap-2 cursor-pointer p-2 bg-amber-50 border border-amber-200 rounded-xl">
                <input
                  type="checkbox"
                  data-testid="deactivate-uncounted-blacklist"
                  checked={addToBlacklist}
                  onChange={(e) => setAddToBlacklist(e.target.checked)}
                  className="w-4 h-4 mt-0.5"
                />
                <span className="text-xs leading-snug">
                  <b>Kara listeye de ekle</b> (önerilmez)
                  <span className="block text-[11px] text-[#1B2A22]/60 mt-0.5">
                    Açık: Akınsoft bir sonraki senkronda bu kodları ATLAR — ürünler pasif kalır.<br/>
                    Kapalı (varsayılan): Akınsoft yeni veri gönderirse ürün otomatik tekrar aktifleşir.
                  </span>
                </span>
              </label>

              {Array.isArray(preview.sample) && preview.sample.length > 0 && (
                <div className="bg-white border border-[#E8E2D5] rounded-xl p-2 max-h-48 overflow-auto">
                  <div className="text-[11px] font-heading font-bold text-[#1B2A22]/60 mb-1">
                    Örnek ({preview.sample.length}):
                  </div>
                  {preview.sample.map((p, i) => (
                    <div key={p.id || i} className="text-xs py-0.5 flex justify-between gap-2 border-b border-[#E8E2D5]/50 last:border-0">
                      <span className="truncate">{p.name_tr}</span>
                      <span className="font-mono text-[10px] text-[#1B2A22]/50">{p.akinsoft_code || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>İptal</Button>
            <Button
              onClick={apply}
              disabled={busy || !preview || preview.to_deactivate === 0}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="deactivate-uncounted-apply"
            >
              {busy ? "Uygulanıyor..." : `⚠ ${preview?.to_deactivate || 0} ürünü PASİFLE`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============ BARCODE IMAGES ZIP UPLOAD ============
// Admin, barkod isimli görselleri (örn. 8690100171234.jpg) ZIP içinde yükler;
// backend barkoddan ürünü bulup image_url'i günceller + image_locked yapar.
function BarcodeImagesZipUpload({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  // AI ile toplu temizleme — işaretliyse ZIP tarayıcıda açılır, her görsel AI'dan
  // geçip kurumsal şablona oturtulur, yeni ZIP paketlenip aynı endpoint'e gönderilir
  const [bulkAi, setBulkAi] = useState(false);
  const [aiProgress, setAiProgress] = useState({ done: 0, total: 0, label: "" });
  const inputRef = React.useRef(null);

  const pickFile = () => inputRef.current?.click();

  // ---- Tek bir barkod görselini AI ile temizle + kurumsal şablona oturt ----
  // Preset'ten konum/opasite/mode okur; AI aktifse fon silinmiş sürüm, değilse
  // orijinal görsel krem zemin üstüne yerleştirilir. 1024×1024 JPEG blob döner.
  const processOne = async (fileBlob, removeBackground, logoImg) => {
    const prefs = (await import("@/components/admin/ImageComposer"))
      .loadComposerPrefs ? (await import("@/components/admin/ImageComposer")).loadComposerPrefs()
      : { bgMode: "ai", blurAmount: 28, logoPos: "br", logoOpacity: 0.35 };
    // Toplu modda AI'yı zorla
    const useAI = true;
    let fgSource;
    if (useAI) {
      try {
        const cleaned = await removeBackground(fileBlob);
        fgSource = await blobToImage(cleaned);
      } catch {
        fgSource = await blobToImage(fileBlob);
      }
    } else {
      fgSource = await blobToImage(fileBlob);
    }
    const OUT = 1024;
    const cvs = document.createElement("canvas");
    cvs.width = OUT; cvs.height = OUT;
    const ctx = cvs.getContext("2d");
    // Krem zemin
    ctx.fillStyle = "#FAF7F2";
    ctx.fillRect(0, 0, OUT, OUT);
    // Ürün — contain-fit, biraz padding
    const target = OUT * 0.88;
    const sc = target / Math.max(fgSource.width, fgSource.height);
    const w = fgSource.width * sc;
    const h = fgSource.height * sc;
    ctx.save();
    ctx.shadowColor = "rgba(27,42,34,0.18)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 8;
    ctx.drawImage(fgSource, (OUT - w) / 2, (OUT - h) / 2, w, h);
    ctx.restore();
    // Logo (ÜSTTE)
    if (logoImg && prefs.logoOpacity > 0) {
      const pad = 32;
      const lw = Math.min(OUT * (prefs.logoPos === "center" ? 0.55 : 0.22), logoImg.width);
      const lh = (lw / logoImg.width) * logoImg.height;
      let lx, ly;
      switch (prefs.logoPos) {
        case "tl": lx = pad; ly = pad; break;
        case "tr": lx = OUT - lw - pad; ly = pad; break;
        case "bl": lx = pad; ly = OUT - lh - pad; break;
        case "br": lx = OUT - lw - pad; ly = OUT - lh - pad; break;
        default:   lx = (OUT - lw) / 2; ly = (OUT - lh) / 2;
      }
      ctx.save();
      ctx.globalAlpha = prefs.logoOpacity;
      ctx.drawImage(logoImg, lx, ly, lw, lh);
      ctx.restore();
    }
    return await new Promise((res) => cvs.toBlob((b) => res(b), "image/jpeg", 0.92));
  };

  const blobToImage = (b) => new Promise((res, rej) => {
    const url = URL.createObjectURL(b);
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = url;
  });

  const loadLogo = () => new Promise((res) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = "/logo-ikizler.png";
  });

  const upload = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Lütfen .zip dosyası seçin");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      let toSend = f;
      // ----- AI toplu temizleme işaretliyse: ZIP'i tarayıcıda yeniden paketle -----
      if (bulkAi) {
        setAiProgress({ done: 0, total: 0, label: "ZIP açılıyor…" });
        const [JSZipMod, bgRemoveMod, logoImg] = await Promise.all([
          import("jszip"),
          import("@imgly/background-removal"),
          loadLogo(),
        ]);
        const JSZip = JSZipMod.default || JSZipMod;
        const removeBackground = bgRemoveMod.default || bgRemoveMod.removeBackground;
        const srcZip = await JSZip.loadAsync(f);
        const entries = [];
        srcZip.forEach((relPath, entry) => {
          if (entry.dir) return;
          const name = relPath.replace(/\\/g, "/").split("/").pop() || "";
          const lower = name.toLowerCase();
          if (!/\.(jpe?g|png|webp)$/i.test(lower)) return;
          entries.push({ name, entry });
        });
        if (entries.length === 0) {
          toast.error("ZIP içinde desteklenen görsel bulunamadı");
          setBusy(false);
          return;
        }
        setAiProgress({ done: 0, total: entries.length, label: "AI model yükleniyor…" });
        const outZip = new JSZip();
        for (let i = 0; i < entries.length; i++) {
          const { name, entry } = entries[i];
          setAiProgress({ done: i, total: entries.length, label: `AI: ${name}` });
          try {
            const inBlob = await entry.async("blob");
            const outBlob = await processOne(inBlob, removeBackground, logoImg);
            // Dosya adının uzantısını .jpg yap (composite JPEG)
            const stem = name.replace(/\.(jpe?g|png|webp)$/i, "");
            outZip.file(`${stem}.jpg`, outBlob);
          } catch (err) {
            console.warn("AI işleme hatası", name, err);
            // Hata varsa orijinali ekle — kayıp olmasın
            try {
              const raw = await entry.async("blob");
              outZip.file(name, raw);
            } catch {}
          }
        }
        setAiProgress({ done: entries.length, total: entries.length, label: "Yeni ZIP hazırlanıyor…" });
        const packed = await outZip.generateAsync({ type: "blob" });
        toSend = new File([packed], f.name.replace(/\.zip$/i, "_ai.zip"), { type: "application/zip" });
        setAiProgress({ done: 0, total: 0, label: "" });
      }

      const fd = new FormData();
      fd.append("file", toSend);
      const { data } = await api.post(
        "/admin/products/upload-barcode-images-zip",
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 },
      );
      setResult(data);
      setOpen(true);
      toast.success(data.message || `✓ ${data.matched} ürün güncellendi`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setBusy(false);
      setAiProgress({ done: 0, total: 0, label: "" });
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="barcode-zip-input"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={pickFile}
          className="rounded-full border-[#E5A024] text-[#1B2A22] bg-white hover:bg-[#E5A024]/10 font-heading font-bold"
          data-testid="barcode-zip-upload-btn"
          title="Barkod isimli görsellerin bulunduğu ZIP dosyasını yükle (örn: 8690100171234.jpg)"
        >
          {busy
            ? aiProgress.total
              ? `🤖 ${aiProgress.done}/${aiProgress.total}`
              : "⏳ Yükleniyor..."
            : "📦 Barkod Fotoğraf ZIP"}
        </Button>
        <label
          className="inline-flex items-center gap-1 text-xs cursor-pointer px-2 py-1 rounded-full border border-[#E8E2D5] bg-white hover:bg-[#2E5A36]/5"
          title="Her görselin arka planını AI ile sil + kurumsal şablona otur, sonra toplu yükle (yavaş — ilk kullanımda ~90MB model indirir)"
        >
          <input
            type="checkbox"
            checked={bulkAi}
            onChange={(e) => setBulkAi(e.target.checked)}
            disabled={busy}
            className="w-3 h-3 accent-[#2E5A36]"
            data-testid="barcode-zip-bulk-ai"
          />
          <span className="font-heading font-bold text-[#2E5A36]">🤖 AI ile hepsini temizle</span>
        </label>
        {busy && aiProgress.label && (
          <span className="text-[11px] text-[#1B2A22]/70 font-mono truncate max-w-[260px]">
            {aiProgress.label}
          </span>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] border-[#E8E2D5] max-w-lg" data-testid="barcode-zip-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-[#2E5A36]">
              📦 ZIP Yükleme Sonucu
            </DialogTitle>
            <DialogDescription>
              Barkod eşleşmesiyle güncellenen ürünlerin özeti.
            </DialogDescription>
          </DialogHeader>
          {result && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#2E5A36]/10 border border-[#2E5A36]/30 rounded-xl p-3">
                  <div className="text-[11px] font-heading text-[#2E5A36]">EŞLEŞEN & GÜNCELLENDİ</div>
                  <div className="text-2xl font-heading font-bold text-[#2E5A36]">{result.matched}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="text-[11px] font-heading text-amber-700">EŞLEŞMEDİ (barkod yok)</div>
                  <div className="text-2xl font-heading font-bold text-amber-700">{result.unknown_count}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#1B2A22]/70 space-y-0.5">
                <div>Toplam dosya: <b>{result.files_in_zip}</b></div>
                {result.skipped_locked > 0 && <div>🔒 Admin kilitli (atlandı): {result.skipped_locked}</div>}
                {result.skipped_too_big > 0 && <div>⚠ Çok büyük (atlandı): {result.skipped_too_big}</div>}
                {result.skipped_bad_ext > 0 && <div>❌ Desteksiz uzantı: {result.skipped_bad_ext}</div>}
              </div>
              {Array.isArray(result.unknown_barcodes) && result.unknown_barcodes.length > 0 && (
                <div className="bg-white border border-amber-200 rounded-xl p-2 max-h-40 overflow-auto">
                  <div className="text-[11px] font-heading font-bold text-amber-700 mb-1">
                    İlk {result.unknown_barcodes.length} eşleşmeyen barkod:
                  </div>
                  <div className="font-mono text-[11px] space-y-0.5">
                    {result.unknown_barcodes.map((b, i) => (
                      <div key={i}>{b}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end mt-3">
            <Button onClick={() => setOpen(false)} className="rounded-full">Tamam</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============ OPEN FOOD FACTS IMAGE MATCHER ============
function OffImageMatcher() {
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [recent, setRecent] = useState({ total: 0, items: [] });
  const [recentOpen, setRecentOpen] = useState(false);

  const refreshStatus = async () => {
    try {
      const r = await api.get("/admin/products/match-images/status");
      setStatus(r.data);
      if (!r.data.running && polling) {
        setPolling(false);
        toast.success(`OFF eşleştirme tamam! ${r.data.matched}/${r.data.processed} ürüne görsel bulundu`);
        refreshRecent();
      }
    } catch {}
  };

  const refreshRecent = async () => {
    try {
      const r = await api.get("/admin/products/match-images/recent?limit=120");
      setRecent(r.data || { total: 0, items: [] });
    } catch {}
  };

  useEffect(() => {
    refreshStatus();
    refreshRecent();
    if (!polling) return;
    const i = setInterval(refreshStatus, 3000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [polling]);

  const start = async () => {
    if (!window.confirm("Open Food Facts'tan barkodu olan ürünler için otomatik görsel çek. Bu birkaç dakika sürer. Devam?")) return;
    try {
      const r = await api.post("/admin/products/match-images?limit=5000");
      if (!r.data.ok) {
        toast.error(r.data.error || "Başlatılamadı");
        return;
      }
      toast.success("OFF görsel eşleştirme başladı (arka planda)");
      setPolling(true);
    } catch (e) {
      toast.error("Başlatılamadı");
    }
  };

  const running = status?.running;
  const sources = status?.by_source || {};
  const sourceSummary = Object.entries(sources)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${k}:${v}`)
    .join(" · ");
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        data-testid="off-match-btn"
        onClick={start}
        disabled={running}
        variant="outline"
        className="rounded-full border-[#2E5A36] text-[#2E5A36] hover:bg-[#2E5A36]/10"
      >
        <ImageIcon className="w-4 h-4 mr-1" />
        {running
          ? `OFF Çalışıyor... ${status.processed}/${status.total} (bulunan: ${status.matched})`
          : "OFF Görsel Eşleştir (4 kaynak)"}
      </Button>
      {sourceSummary && (
        <span className="text-[10px] font-mono text-[#2E5A36]/80" title="Kaynak başına eşleşen görsel">
          {sourceSummary}
        </span>
      )}
      <Button
        data-testid="off-recent-btn"
        onClick={() => { refreshRecent(); setRecentOpen(true); }}
        variant="outline"
        className="rounded-full border-[#E5A024] text-[#B4711C] hover:bg-[#E5A024]/10"
        title="OFF ile görsel kazanan ürünleri gör"
      >
        <Eye className="w-4 h-4 mr-1" />
        {recent.total > 0 ? `${recent.total} görsel bulundu` : "Bulunan Görseller"}
      </Button>
      <Button
        data-testid="off-apply-all-btn"
        onClick={async () => {
          if (running) { toast.message("Zaten çalışıyor"); return; }
          if (!window.confirm(
            `Tümünü Uygula?\n\n` +
            `• ${recent.no_match_cooldown || 0} bekleyen 'bulunamadı' cooldown'u sıfırlanacak\n` +
            `• ${recent.pending_to_try || 0} denenecek ürün için OFF eşleştirmesi başlatılacak`,
          )) return;
          try {
            const r = await api.post("/admin/products/match-images/retry-failed");
            toast.success(`✓ ${r.data?.reset || 0} cooldown sıfırlandı`);
            const r2 = await api.post("/admin/products/match-images?limit=5000");
            if (r2.data?.ok) {
              toast.success("OFF eşleştirme başladı");
              setPolling(true);
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Başlatılamadı");
          }
        }}
        disabled={running}
        className="rounded-full bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold"
        title="Cooldown'daki başarısızları sıfırla + tüm bekleyenleri OFF ile dene"
      >
        ⚡ Tümünü Uygula
      </Button>
      {/* Sayaçlar — site üzerinde görünen, pasif/gizli, beklemede ve denenecek */}
      <div className="flex items-center gap-1 flex-wrap text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-[#2E5A36]/10 border border-[#2E5A36]/30 text-[#2E5A36] font-heading font-bold" title="OFF ile görsel kazanmış ve sitede müşteriye görünen ürünler">
          👁 Sitede: <span className="font-mono">{recent.visible_on_site ?? 0}</span>
        </span>
        <span className="px-2 py-0.5 rounded-full bg-[#1B2A22]/10 border border-[#1B2A22]/30 text-[#1B2A22]/70 font-heading" title="OFF görseli olan ama pasif / gizli ürünler">
          ⛔ Pasif/Gizli: <span className="font-mono">{recent.hidden_inactive ?? 0}</span>
        </span>
        <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 font-heading" title="OFF daha önce denendi, görsel bulunamadı (cooldown)">
          ⏸ Cooldown: <span className="font-mono">{recent.no_match_cooldown ?? 0}</span>
        </span>
        <span className="px-2 py-0.5 rounded-full bg-[#D9653B]/10 border border-[#D9653B]/40 text-[#D9653B] font-heading font-bold" title="OFF henüz denenmedi — barkodlu + aktif + görselsiz">
          🔄 Denenecek: <span className="font-mono">{recent.pending_to_try ?? 0}</span>
        </span>
      </div>
      {status?.finished_at && !running && (
        <span className="text-xs text-[#2E5A36]">✓ Son çalıştırma: {status.matched}/{status.processed}</span>
      )}

      <Dialog open={recentOpen} onOpenChange={setRecentOpen}>
        <DialogContent data-testid="off-recent-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#2E5A36]" />
              OFF ile Eşleşen Ürünler ({recent.total})
            </DialogTitle>
          </DialogHeader>
          {recent.items.length === 0 ? (
            <div className="text-center py-10 text-[#1B2A22]/60">
              Henüz OFF ile eşleşen ürün yok. "OFF Görsel Eşleştir" düğmesine bas.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {recent.items.map((p) => (
                <div
                  key={p.id}
                  data-testid={`off-recent-item-${p.id}`}
                  className="bg-white border border-[#E8E2D5] rounded-xl p-2 flex flex-col items-center text-center"
                >
                  <img
                    src={p.image_url}
                    alt={p.name_tr}
                    className="w-full h-24 object-cover rounded-lg bg-[#FAF7F2]"
                    loading="lazy"
                  />
                  <p className="text-xs font-semibold mt-2 line-clamp-2">{p.name_tr}</p>
                  {p.brand && <p className="text-[10px] text-[#D9653B] font-bold mt-0.5">{p.brand}</p>}
                  {p.barcode && <p className="text-[10px] text-[#1B2A22]/50 font-mono mt-0.5">{p.barcode}</p>}
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-[#1B2A22]/60 pt-2 border-t border-[#E8E2D5]">
            💡 İlk 120 ürün gösteriliyor. Hepsini görmek için <b>Ürünler</b> sekmesinde kategoriye veya ada göre filtrele.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============ PRODUCTS PANEL (search + multi-select + bulk delete) ============
function ProductsPanel({ products, categories, onReload, onEdit, onCreate, onScan, overrideCatFilter, onConsumeOverride }) {  const { t } = useApp();
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [groupReportOpen, setGroupReportOpen] = useState(false);
  const [filterDeleteOpen, setFilterDeleteOpen] = useState(false);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistPending, setBlacklistPending] = useState(0);
  const [imageDialogProduct, setImageDialogProduct] = useState(null);
  const [imagePushPending, setImagePushPending] = useState(0);
  const PAGE_SIZE = 100;

  // Admin header "Kategori Bekleyen" rozetine basılırsa katFilter'ı dışarıdan ayarla.
  React.useEffect(() => {
    if (overrideCatFilter) {
      setCatFilter(overrideCatFilter);
      setQ("");
      setPage(1);
      try { onConsumeOverride && onConsumeOverride(); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideCatFilter]);

  // Poll pending-restore count: Wolvox trying to re-send previously-deleted
  // codes. 60s cadence keeps load tiny; also refreshes right after the user
  // closes the Kara Liste dialog so an action-driven update is instant.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [b, i] = await Promise.all([
          api.get("/admin/products/blacklist/pending-count"),
          api.get("/admin/products/pending-image-push-count"),
        ]);
        if (!cancelled) {
          setBlacklistPending(b.data?.pending || 0);
          setImagePushPending(i.data?.pending || 0);
        }
      } catch {}
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const refreshPending = async () => {
    try {
      const [b, i] = await Promise.all([
        api.get("/admin/products/blacklist/pending-count"),
        api.get("/admin/products/pending-image-push-count"),
      ]);
      setBlacklistPending(b.data?.pending || 0);
      setImagePushPending(i.data?.pending || 0);
    } catch {}
  };

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter((p) => {
      if (catFilter !== "all" && p.category_slug !== catFilter) return false;
      if (!s) return true;
      return (
        (p.name_tr || "").toLowerCase().includes(s) ||
        (p.brand || "").toLowerCase().includes(s) ||
        (p.barcode || "").includes(s) ||
        (p.akinsoft_code || "").includes(s)
      );
    });
  }, [products, q, catFilter]);

  const visible = filtered.slice(0, page * PAGE_SIZE);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      const allSelected = visible.every((p) => n.has(p.id));
      if (allSelected) visible.forEach((p) => n.delete(p.id));
      else visible.forEach((p) => n.add(p.id));
      return n;
    });
  };
  const clearSel = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} ürün SİLİNECEK. Bu işlem geri alınamaz. Devam?`)) return;
    try {
      const { data } = await api.post("/admin/products/bulk-delete", { ids });
      toast.success(`✓ ${data.deleted} ürün silindi`);
      clearSel();
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Ürün silindi");
      onReload();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const allVisibleSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));

  return (
    <>
      <div className="flex flex-wrap justify-between gap-2 mb-3">
        <div className="flex gap-2 items-center flex-wrap">
          <ExcelImport onDone={onReload} />
          <OffImageMatcher />
          <BarcodeImagesZipUpload onDone={onReload} />
          {catFilter === "yeni-urunler" && (
            <div
              data-testid="needs-category-filter-chip"
              className="inline-flex items-center gap-2 bg-red-600 text-white rounded-full px-3 py-1 text-sm font-heading font-bold shadow"
              title="Admin header rozetinden geldiniz — bu ürünlere kategori atayın"
            >
              🆕 Kategori Bekleyen Ürünler
              <button
                onClick={() => { setCatFilter("all"); setPage(1); }}
                className="ml-1 hover:bg-white/20 rounded-full w-5 h-5 flex items-center justify-center"
                title="Filtreyi kaldır"
                data-testid="needs-category-filter-clear"
              >
                ×
              </button>
            </div>
          )}
          <Button
            data-testid="wolvox-group-report-btn"
            onClick={() => setGroupReportOpen(true)}
            variant="outline"
            className="rounded-full border-[#E5A024] text-[#B4711C] hover:bg-[#E5A024]/10"
            title="Wolvox'tan gelen grupların site kategorilerine nasıl eşleştiğini gör"
          >
            📊 Wolvox Grup Raporu
          </Button>
          <Button
            data-testid="filter-delete-btn"
            onClick={() => setFilterDeleteOpen(true)}
            variant="outline"
            className="rounded-full border-red-500 text-red-600 hover:bg-red-50"
            title="Fiyat / hareket tarihi / kategori filtresi ile toplu silme"
          >
            🧹 Filtre ile Toplu Sil
          </Button>
          <Button
            data-testid="blacklist-btn"
            onClick={() => setBlacklistOpen(true)}
            variant="outline"
            className={`rounded-full ${blacklistPending > 0 ? "border-[#D9653B] text-[#D9653B] bg-[#D9653B]/5 hover:bg-[#D9653B]/15 animate-pulse" : "border-[#1B2A22] text-[#1B2A22]/70 hover:bg-[#1B2A22]/5"}`}
            title={blacklistPending > 0 ? `Wolvox ${blacklistPending} silinmiş ürünü tekrar göndermeye çalışıyor — incele` : "Silinmiş ürünler listesi"}
          >
            🚫 Kara Liste
            {blacklistPending > 0 && (
              <span data-testid="blacklist-pending-badge" className="ml-2 bg-[#D9653B] text-white rounded-full text-[10px] px-2 py-0.5 font-heading font-bold">
                ⚠️ {blacklistPending}
              </span>
            )}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="scan-barcode-btn"
            onClick={onScan}
            variant="outline"
            className="rounded-full border-[#2E5A36] text-[#2E5A36] hover:bg-[#2E5A36]/10"
          >
            <ScanLine className="w-4 h-4 mr-1" /> Barkod Oku
          </Button>
          <Button
            data-testid="add-product-btn"
            onClick={onCreate}
            className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full"
          >
            <Plus className="w-4 h-4 mr-1" /> {t("add_product")}
          </Button>
        </div>
      </div>

      {/* Search + filter + bulk action bar */}
      <div className="bg-white border border-[#E8E2D5] rounded-2xl p-3 mb-3 flex flex-wrap gap-2 items-center">
        <Input
          data-testid="admin-product-search"
          placeholder="Ürün ara (ad, marka, barkod, akınsoft kod)..."
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          className="bg-[#FAF7F2] flex-1 min-w-[240px]"
        />
        <Select value={catFilter} onValueChange={(v) => { setCatFilter(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-product-cat-filter" className="w-56 bg-[#FAF7F2]">
            <SelectValue placeholder="Tüm Kategoriler" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>
                {c.icon || "📦"} {c.name_tr}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-[#1B2A22]/70 font-mono">
          {visible.length} / {filtered.length}
        </div>
        {selected.size > 0 && (
          <>
            <div className="ml-auto flex items-center gap-2 bg-[#D9653B]/10 border border-[#D9653B]/40 rounded-full px-3 py-1">
              <Trash2 className="w-4 h-4 text-[#D9653B]" />
              <span className="text-sm font-heading font-bold text-[#D9653B]">
                {selected.size} seçili
              </span>
            </div>
            <Button
              data-testid="bulk-move-btn"
              onClick={() => setMoveDialogOpen(true)}
              className="bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full"
              title="Seçili ürünleri bir kategoriye taşı (varsayılan: Wolvox sync'ten koru)"
            >
              <FolderTree className="w-4 h-4 mr-1" /> Kategoriye Taşı ({selected.size})
            </Button>
            <Button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Seçilenleri Sil ({selected.size})
            </Button>
            <Button
              data-testid="bulk-clear-btn"
              onClick={clearSel}
              variant="outline"
              className="rounded-full"
            >
              <X className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>

      <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  data-testid="bulk-select-all"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  className="w-4 h-4 accent-[#2E5A36]"
                  title="Görünenlerin tümünü seç"
                />
              </TableHead>
              <TableHead>Ürün</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Fiyat</TableHead>
              <TableHead>Eski Fiyat</TableHead>
              <TableHead>Stok</TableHead>
              <TableHead>Kampanya</TableHead>
              <TableHead>Barkod</TableHead>
              <TableHead>Akınsoft</TableHead>
              <TableHead>🔒</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-[#1B2A22]/60">
                  {products.length === 0 ? "Henüz ürün yok." : "Aramaya uyan ürün yok."}
                </TableCell>
              </TableRow>
            )}
            {visible.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`} className={`${selected.has(p.id) ? "bg-[#D9653B]/5" : ""} ${p.is_active === false ? "opacity-60" : ""}`}>
                <TableCell>
                  <input
                    type="checkbox"
                    data-testid={`product-select-${p.id}`}
                    checked={selected.has(p.id)}
                    onChange={() => toggle(p.id)}
                    className="w-4 h-4 accent-[#2E5A36]"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setImageDialogProduct(p)}
                      className="relative w-10 h-10 shrink-0 rounded-lg overflow-hidden border border-[#E8E2D5] bg-white hover:ring-2 hover:ring-[#2E5A36] transition flex items-center justify-center"
                      title="Görseli değiştir / Akınsoft'a gönder"
                      data-testid={`product-image-btn-${p.id}`}
                    >
                      {p.image_url ? (
                        /* key by url so React remounts <img> on url change
                           instead of trying to swap src mid-render, which can
                           collide with Radix dialog closing animations */
                        <img key={p.image_url} src={p.image_url} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <span className="text-[#1B2A22]/40 text-lg">📷</span>
                      )}
                      {p.image_locked && (
                        <span className="absolute -top-1 -right-1 bg-[#E5A024] text-white rounded-full w-4 h-4 text-[8px] flex items-center justify-center font-bold pointer-events-none" title="Kilitli">🔒</span>
                      )}
                      {p.pending_push_to_wolvox && (
                        <span className="absolute -bottom-1 -right-1 bg-[#D9653B] text-white rounded-full w-4 h-4 text-[8px] flex items-center justify-center font-bold animate-pulse pointer-events-none" title="Akınsoft'a push bekliyor">🔄</span>
                      )}
                    </button>
                    <div className="flex flex-col">
                      <span className="font-semibold">{p.name_tr}</span>
                      {p.is_active === false && (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-red-600 text-white rounded-full px-2 py-0.5 font-heading font-bold w-fit mt-0.5" title={p.deactivated_reason === "count_cycle_uncounted" ? "Sayım döngüsünde pasifleştirildi" : "Pasif"}>
                          ⛔ PASİF{p.deactivated_reason === "count_cycle_uncounted" ? " (sayım)" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {categories.find((c) => c.slug === p.category_slug)?.name_tr || p.category_slug}
                </TableCell>
                <TableCell className="font-mono font-bold">₺{p.price.toFixed(2)}</TableCell>
                <TableCell className="font-mono text-xs text-[#1B2A22]/60">
                  {p.old_price ? `₺${p.old_price.toFixed(2)}` : "—"}
                </TableCell>
                <TableCell className="font-mono">{p.stock}</TableCell>
                <TableCell>
                  {p.is_weekly_deal && <Badge className="bg-[#E5A024] text-white">🔥 Hafta</Badge>}
                  {p.is_campaign && !p.is_weekly_deal && <Badge className="bg-[#D9653B] text-white">%</Badge>}
                </TableCell>
                <TableCell className="font-mono text-xs">{p.barcode || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.akinsoft_code || "-"}</TableCell>
                <TableCell className="text-center" title={p.category_locked ? "Kategori kilitli — Wolvox sync bozmaz" : ""}>
                  {p.category_locked ? "🔒" : ""}
                </TableCell>
                <TableCell className="flex gap-1">
                  <Button data-testid={`edit-product-${p.id}`} size="sm" variant="outline" onClick={() => onEdit(p)} className="rounded-full">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  {p.is_active === false && (
                    <Button
                      data-testid={`activate-product-${p.id}`}
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await api.patch(`/products/${p.id}`, { is_active: true });
                          toast.success(`✓ ${p.name_tr} aktifleştirildi`);
                          onReload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Aktifleştirilemedi");
                        }
                      }}
                      className="rounded-full text-[#2E5A36] border-[#2E5A36]"
                      title="Pasif ürünü hemen aktifleştir"
                    >
                      ✓
                    </Button>
                  )}
                  <Button data-testid={`delete-product-${p.id}`} size="sm" variant="outline" onClick={() => deleteOne(p.id)} className="rounded-full text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {visible.length < filtered.length && (
        <div className="flex justify-center mt-3">
          <Button
            data-testid="admin-products-load-more"
            onClick={() => setPage((v) => v + 1)}
            variant="outline"
            className="rounded-full border-[#2E5A36] text-[#2E5A36]"
          >
            Daha Fazla Göster ({filtered.length - visible.length})
          </Button>
        </div>
      )}

      <BulkMoveCategoryDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        selectedIds={Array.from(selected)}
        categories={categories}
        onDone={() => { clearSel(); onReload(); }}
      />
      <WolvoxGroupReportDialog
        open={groupReportOpen}
        onOpenChange={setGroupReportOpen}
        categories={categories}
        onApplied={onReload}
      />
      <FilterDeleteDialog
        open={filterDeleteOpen}
        onOpenChange={setFilterDeleteOpen}
        categories={categories}
        onDone={() => { clearSel(); onReload(); }}
      />
      <BlacklistDialog
        open={blacklistOpen}
        onOpenChange={(v) => { setBlacklistOpen(v); if (!v) refreshPending(); }}
        onRestored={() => { onReload(); refreshPending(); }}
      />
      <ProductImageDialog
        open={!!imageDialogProduct}
        onOpenChange={(v) => {
          if (!v) {
            // Delay nulling product until Radix finishes closing animation
            // (~200ms). If we null immediately, <img> inside the dialog
            // unmounts mid-animation and can race with Radix's portal
            // removeChild → "node is not a child of this node" crash.
            setTimeout(() => setImageDialogProduct(null), 220);
          }
        }}
        product={imageDialogProduct}
        onSaved={() => { onReload(); refreshPending(); }}
      />
    </>
  );
}

